<template>
    <div id="content" :class="[labelSelected ? 'label-print' : 'label']">
        <template v-if="!queueSelected && !labelSelected && aStatesToPrint.length == 0">
            <template v-if="['portrait'].includes($mq)">
                <div class="portrait-label-container">
                    <!-- BUILD-7561: atrás + título + buscar. Encima de KPI (BUILD-7483) y filtros. -->
                    <TimerProductListHeader :value="debounceSearchInput" @back="backToTimers" @search="onHeaderSearch" @clear="cleanSearch" @open="onHeaderSearchOpen" />
                    <TimerProductKpiCards :show-external-link="false" />

                    <div class="categories-container hide-scrollbar">
                        <div class="portrait-categories" v-if="Object.values(categories).length != 0" :class="bCategories ? 'column-withtab' : ''">
                            <!-- BUILD-7555: filtros dropdown + grid directo (sin pantalla intermedia de categorías) -->
                            <div class="label-content">
                                <!-- BUILD-7643: props/eventos de colecciones se mantienen; el dropdown se oculta en el hijo. -->
                                <TimerProductPortraitFilters
                                    :categories="portraitFilterCategories"
                                    :statuses="portraitFilterStatuses"
                                    :collections="portraitFilterCollections"
                                    :selected-category-id="portraitSelectedCategoryId"
                                    :selected-status-id="stateSelected"
                                    :selected-collection-id="sSelectedCollection"
                                    @select-category="onPortraitFilterCategory"
                                    @select-status="onPortraitFilterStatus"
                                    @select-collection="onPortraitFilterCollection"
                                />
                                <div v-if="showSelectAllProductsButton && showPortraitProductGrid" class="products-select-all">
                                    <button
                                        type="button"
                                        class="products-select-all__action"
                                        @click="areAllVisibleProductsSelected ? deselectAllVisibleProducts() : selectAllVisibleProducts()"
                                    >
                                        <span class="products-select-all__icon" aria-hidden="true"></span>
                                        <span class="products-select-all__label">
                                            {{ areAllVisibleProductsSelected ? $t('timersproduct.deselect_all_products') : $t('timersproduct.select_all_products') }}
                                        </span>
                                    </button>
                                </div>
                                <div v-if="showPortraitProductGrid" class="container scrollbar" ref="container" id="label_container">
                                    <template v-if="products && num_columns">
                                        <virtual-list ref="labelItems" :keeps="portraitListKeeps" :estimate-size="productListEstimateSize" class="virtual-list list-keep scroll-touch hide-scrollbar" :item-class="'list-item-keep'" :data-key="'id'" :data-sources="productsFiltered" :data-component="labelComponent" :extra-props="labelListExtraProps" />
                                    </template>
                                </div>
                                <div v-else-if="currentCollection" class="content hide-scrollbar right" id="collections" :key="sSelectedCollection">
                                    <template v-for="reference in sortByName(currentCollection.references)" :reference="reference">
                                        <ReferenceState
                                            v-for="state in sortByPriority(reference.states)"
                                            :key="reference.id + state.id + sSelectedCollection"
                                            :state="state"
                                            :collectionID="sSelectedCollection"
                                            :referenceID="reference.id"
                                            :referenceName="reference.name"
                                            :referenceImage="reference.image"
                                            :show-images="showImages"
                                            @showPrintButton="checkShowPrintButton()"
                                        ></ReferenceState>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <div class="empty-page" v-if="Object.values(categories).length == 0">
                            <EmptyPage :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.product_description')"></EmptyPage>
                        </div>
                    </div>
                    <!-- BUILD-7488 / BUILD-7627: barra de siguiente fuera del scroll (solo KPI Cards quedan fijas arriba) -->
                    <div v-if="showMultiSelectNextButton && showPortraitProductGrid" class="products-multi-next">
                        <div ref="multiSelectSummary" class="products-multi-next__summary">
                            <div v-if="showImages" class="products-multi-next__thumbs" aria-hidden="true">
                                <span
                                    v-for="(product, thumbIndex) in multiSelectedPreviewProducts"
                                    :key="product.id"
                                    class="products-multi-next__thumb"
                                    :style="{ zIndex: thumbIndex + 1 }"
                                    :title="product.name || ''"
                                >
                                    <span class="products-multi-next__thumb-icon" :style="multiSelectThumbIconStyle(product)"></span>
                                </span>
                                <span
                                    v-if="multiSelectedOverflowCount > 0"
                                    class="products-multi-next__thumb products-multi-next__thumb--overflow"
                                    :style="{ zIndex: multiSelectedPreviewProducts.length + 1 }"
                                >
                                    +{{ multiSelectedOverflowCount }}
                                </span>
                            </div>
                            <span ref="multiSelectCount" class="products-multi-next__count">
                                {{ $tc('timersproduct.products_selected', multiSelectedCount, { n: multiSelectedCount }) }}
                            </span>
                        </div>
                        <button type="button" class="products-multi-next__action" :disabled="isPreparingTemporalCollection" @click="confirmMultiSelectProducts">
                            {{ $t('timersproduct.next') }}
                        </button>
                    </div>
                </div>
            </template>
            <template v-else>
                <!-- Desktop / landscape: diseño anterior (columna de categorías + chips de estados) -->
                <div v-if="!activePredef" class="content hide-scrollbar" :class="Object.values(categories).length == 0 ? 'full' : 'right'">
                    <TimerProductKpiCards ref="kpiCards" :show-external-link="false" />
                    <template v-if="iTabulationType == 1">
                        <div class="states" id="states" :class="{ 'alert-active': printerError }">
                            <div class="state-options hide-scrollbar" ref="state_options">
                                <span class="state back_button" ref="back" @click="backToTimers()"></span>

                                <span v-if="Object.values(categories).length != 0" class="state" ref="state_all" :class="{ selected: stateSelected == false }" @click="stateSelected = false">{{ $t('label.all') }}</span>

                                <span
                                    class="state"
                                    v-for="(state, index) in productStates"
                                    :class="{
                                        selected: state.id == stateSelected,
                                        hide: !showMoreStates && numberOfStatesToShow !== false && (numberOfStatesToShow == 0 || numberOfStatesToShow < index + 1),
                                        transparent: numberOfStatesToShow === false
                                    }"
                                    @click="stateSelected = stateSelected == state.id ? false : state.id"
                                    ref="states"
                                    :key="index"
                                >
                                    {{ state.name }}
                                </span>

                                <span
                                    v-if="numberOfStatesToShow < productStates.length"
                                    ref="state_more"
                                    class="state display"
                                    :class="{
                                        transparent: numberOfStatesToShow === false,
                                        less: showMoreStates
                                    }"
                                    @click="showMoreStates = !showMoreStates"
                                >
                                    <template v-if="showMoreStates">
                                        {{ $t('label.less') }}
                                    </template>
                                    <template v-else>
                                        {{ $t('label.more') }}
                                    </template>
                                </span>

                                <div class="empty-page" v-if="Object.values(categories).length == 0">
                                    <EmptyPage :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.product_description')"></EmptyPage>
                                </div>
                            </div>
                        </div>

                        <div class="search-container" v-if="Object.values(categories).length > 0 && sSelectedCollection == -1">
                            <input class="search" type="text" v-model="debounceSearchInput" @input="searchBar(debounceSearchInput)" />
                            <div v-if="searchInput != ''" class="close" @click="cleanSearch()"></div>
                        </div>

                        <div v-if="showSelectAllProductsButton && sSelectedCollection == -1" class="products-select-all">
                            <button
                                type="button"
                                class="products-select-all__action"
                                @click="areAllVisibleProductsSelected ? deselectAllVisibleProducts() : selectAllVisibleProducts()"
                            >
                                <span class="products-select-all__icon" aria-hidden="true"></span>
                                <span class="products-select-all__label">
                                    {{ areAllVisibleProductsSelected ? $t('timersproduct.deselect_all_products') : $t('timersproduct.select_all_products') }}
                                </span>
                            </button>
                        </div>

                        <div
                            v-if="Object.values(categories).length != 0 && sSelectedCollection == -1"
                            class="container scrollbar"
                            ref="container"
                            id="label_container"
                            :style="labelContainerHeightStyle"
                            :class="{ 'alert-active': printerError }"
                        >
                            <template v-if="products">
                                <template v-if="num_columns">
                                    <virtual-list ref="labelItems" :keeps="8" :estimate-size="productListEstimateSize" class="virtual-list list-keep scroll-touch hide-scrollbar" :item-class="'list-item-keep'" :data-key="'id'" :data-sources="productsFiltered" :data-component="labelComponent" :extra-props="labelListExtraProps" />
                                </template>
                            </template>
                            <p v-else class="empty">
                                {{ $t('label.empty_labels') }}
                            </p>
                        </div>

                        <!-- BUILD-7488: Siguiente arma la colección temporal y entra al flujo de colección -->
                        <div v-if="showMultiSelectNextButton && sSelectedCollection == -1" class="products-multi-next">
                            <div ref="multiSelectSummary" class="products-multi-next__summary">
                                <div v-if="showImages" class="products-multi-next__thumbs" aria-hidden="true">
                                    <span
                                        v-for="(product, thumbIndex) in multiSelectedPreviewProducts"
                                        :key="product.id"
                                        class="products-multi-next__thumb"
                                        :style="{ zIndex: thumbIndex + 1 }"
                                        :title="product.name || ''"
                                    >
                                        <span class="products-multi-next__thumb-icon" :style="multiSelectThumbIconStyle(product)"></span>
                                    </span>
                                    <span
                                        v-if="multiSelectedOverflowCount > 0"
                                        class="products-multi-next__thumb products-multi-next__thumb--overflow"
                                        :style="{ zIndex: multiSelectedPreviewProducts.length + 1 }"
                                    >
                                        +{{ multiSelectedOverflowCount }}
                                    </span>
                                </div>
                                <span ref="multiSelectCount" class="products-multi-next__count">
                                    {{ $tc('timersproduct.products_selected', multiSelectedCount, { n: multiSelectedCount }) }}
                                </span>
                            </div>
                            <button type="button" class="products-multi-next__action" :disabled="isPreparingTemporalCollection" @click="confirmMultiSelectProducts">
                                {{ $t('timersproduct.next') }}
                            </button>
                        </div>
                    </template>
                    <template v-else>
                        <div id="collections" v-if="currentCollection" :key="sSelectedCollection">
                            <template v-for="reference in sortByName(currentCollection.references)" :reference="reference">
                                <ReferenceState
                                    v-for="state in sortByPriority(reference.states)"
                                    :key="reference.id + state.id + sSelectedCollection"
                                    :state="state"
                                    :collectionID="sSelectedCollection"
                                    :referenceID="reference.id"
                                    :referenceName="reference.name"
                                    :referenceImage="reference.image"
                                    :show-images="showImages"
                                    @showPrintButton="checkShowPrintButton()"
                                ></ReferenceState>
                            </template>
                        </div>
                    </template>
                </div>

                <div class="column left hide-scrollbar categories" v-if="Object.values(categories).length != 0" :class="bCategories ? 'column-withtab' : ''">
                    <template v-if="iTabulationType == 1">
                        <div class="item all" v-if="sortedCategories" @click="clickCat(false)" :class="{ selected: !category && !activePredef }">
                            <span class="color" v-bind:style="{ backgroundColor: '#475b96' }"></span>
                            <span class="text">{{ $t('label.categories.all') }}</span>
                        </div>
                        <template v-if="sortedCategories">
                            <div
                                v-for="category in sortedCategories"
                                class="item"
                                :class="{ selected: category.selected && !activePredef }"
                                :key="category.id"
                                :style="
                                    category.selected
                                        ? {
                                              backgroundColor: category.selected && !activePredef ? category.color : 'transparent'
                                          }
                                        : {}
                                "
                                @click="clickCat(category)"
                            >
                                <span class="color" v-bind:style="{ backgroundColor: category.color }"></span>
                                <span class="text">{{ category.name }}</span>
                                <span class="count" v-show="getSelectedCatLab(category.id) > 0">{{ getSelectedCatLab(category.id) }}</span>
                            </div>
                        </template>
                    </template>

                    <template v-else>
                        <div v-for="(collection, index) in portraitFilterCollections" :key="collection.id || index">
                            <div class="collection" :class="{ selected: sSelectedCollection == collection.id }" @click="clickCollection(collection.id)">
                                {{ collection.name }}
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </template>

        <template v-else-if="queueSelected">
            <printQueue :printerError="printerError" @initStatusPrinter="initStatusPrinter" @back="closePrintErrorQueue"></printQueue>
        </template>

        <template v-else>
            <print :key="temporalCollectionPrintId || 'timer-single'" :label="labelSelected" :type="'timerproduct'" :show-images="showImages" :states="aStatesToPrint" :labelStateSelected="labelStateSelected" :printer_available="activePrint" :selectedCollection="temporalCollectionPrintId" @print="timerPreparer" @printAll="timerPreparerAll" @unselect="onWizardUnselect"></print>
        </template>
    </div>
</template>

<script>
import { formatPrintedDateFromStore } from '@/utils/dateFormatters'
import LabelBox from '@/components/domain/label/labelBox.vue'
import LabelItem from '@/components/domain/label/labelItem.vue'
import printQueue from '@/components/domain/label/printQueue.vue'
import EmptyPage from '@/components/layout/EmptyPage.vue'
import print from '@/components/domain/label/print'
import ReferenceState from '@/components/domain/label/ReferenceState'
import Message from '@/components/domain/ui/Message'
import TimerProductKpiCards from '@/components/domain/timerproduct/TimerProductKpiCards.vue'
import TimerProductListHeader from '@/components/domain/timerproduct/TimerProductListHeader.vue'
import TimerProductPortraitFilters, { PORTRAIT_STATUS_ALL, SHOW_PORTRAIT_COLLECTION_FILTER } from '@/components/domain/timerproduct/TimerProductPortraitFilters.vue'
import * as bpac from '@/../public/js/bpac.js'
import i18n from '@/i18n'
import VirtualList from 'vue-virtual-scroll-list'
import { buildPrintStatesFromCollection, buildWizardProductReferences, ensureCollectionStateIds, isTemporalCollection, mapPrintAllItemToTimerPayload, resolveProductFromStore, shouldRestoreProductListAfterWizard, snapshotPrintAllItems } from '@/utils/timerProductTemporalCollection'

const MULTI_SELECT_PREVIEW_LIMIT = 3
const PRODUCT_LIST_ROW_SIZE = 155
const PRODUCT_LIST_ROW_SIZE_NO_IMAGES = 86

export default {
    name: 'Products',
    components: {
        print,
        ReferenceState,
        Message,
        EmptyPage,
        printQueue,
        LabelBox,
        VirtualList,
        TimerProductKpiCards,
        TimerProductListHeader,
        TimerProductPortraitFilters
    },
    props: {
        /**
         * BUILD-7493: ids de producto sugeridos. Al montar, abre el wizard
         * (uno = ficha actual; varios = colección temporal existente).
         */
        recommendedProductIds: {
            type: Array,
            default() {
                return []
            }
        },
        /**
         * BUILD-7492: si viene de la combinada, el padre pinta el snackbar
         * (expirados + añadidos). El callback vive en el padre para
         * sobrevivir al unmount de este listado.
         */
        onRecommendationAddResult: {
            type: Function,
            default: null
        },
        /**
         * BUILD-7687: misma preferencia que el board (BUILD-7551).
         * Solo oculta miniaturas; no altera selección ni alta de timers.
         */
        showImages: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            allStates: false,
            stateSwitch: false,
            allCategories: false,
            cantSelectCategoryPrinterError: false,
            holding: false,
            holdingCat: false,
            selectedLab: {},
            /**
             * Estado visual de selección múltiple (BUILD-7485).
             * Aislado de holding/selectedLab para no alterar el flujo de impresión existente.
             * La persistencia/acciones masivas se abordan en BUILD-7488.
             */
            isMultiSelectMode: false,
            multiSelectedProducts: {},
            /**
             * Ignora el click/press fantasma tras long-press (sobre todo en touch).
             * Vive en el padre para sobrevivir a remounts del virtual-list.
             */
            multiSelectIgnoreToggleUntil: 0,
            multiSelectIgnoreToggleProductId: null,
            printQueue: [],
            activePredef: false,
            actTab: false,
            fbar: {},
            activePrint: false,
            printData: [],
            num_copies: 0,
            next_state: false,
            oParams: {},
            labelSelected: false,
            queueSelected: false,
            abc: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'U', 'V', 'W', 'X', 'Y', 'Z'],
            current_letter: '',
            highlight_letter: false,
            scrolling: false,
            scrollWait: false,
            noscroll: false,
            stateSelected: false,
            labelsNoPhoto: true,
            // printer: false,
            // config: false,
            // label: false,
            statesHeight: 45,
            messageHeight: 0,
            iTabulationType: 1, // 1 => category, 2 => collection
            sSelectedCollection: -1,
            bCollectionPrintButton: true,
            aStatesToPrint: [],
            numberOfStatesToShow: false,
            showMoreStates: false,
            labelStateSelected: '',
            iIntervalStatusPrinter: null,
            bSendPrintError: false,
            can_validateprinter: true,
            searchInput: '',
            debounceSearchInput: '',
            timetoPrintW: { i: false, e: false },
            timetoPrintP: { i: false, e: false },
            catSelPortrait: false,
            labelComponent: LabelItem,
            breakpoints: { 350: 1, 700: 2, 1000: 3, 1200: 4, 1500: 5 },
            num_columns: 0,
            timer: false,
            timerSearch: false,
            currentLabelIndex: 0,
            printerError: false,
            availableNumbers: [],
            lastNumber: null,
            currentNumber: null,
            /**
             * Reserva inicial (~una fila de KPI) hasta la primera medición real.
             * Evita que el listado se recorte mientras calcResize aún no ha corrido.
             */
            kpiCardsOffset: 96,
            /**
             * BUILD-7488: evita doble submit al armar la colección temporal.
             */
            isPreparingTemporalCollection: false,
            /**
             * Id de la colección temporal en curso. No se asigna a
             * sSelectedCollection para no mostrar la vista/botón de imprimir.
             */
            temporalCollectionId: null,
            /**
             * Evita doble arranque del wizard de recomendación (watch + overlay).
             */
            isStartingRecommendedAdd: false,
            /**
             * BUILD-7492 / BUILD-7493: el wizard se abrió desde una sugerencia.
             * Cancelar debe volver al board, no al listado de productos.
             */
            openedFromRecommendation: false,
            /**
             * Alta en curso (printAll o generateTimer). Evita tratar el
             * unselect post-creación como cancelar.
             */
            isConfirmingAdd: false
        }
    },
    computed: {
        allProductStates() {
            // Estados de producto (label.states), no columnas del tablero.
            // getStates incluye descarte (id: false, mismo sentinel que “todos”)
            // y ids _channel que no existen en label.states / productsFiltered.
            return this.productStates.filter((status) => status && status.id !== false && status.id != null && status.id !== '')
        },

        checkWindows() {
            return getOS() == 'Windows'
        },

        getRoleLoggedUser() {
            var user = this.$store.getters['loginUser/getLocalEmployee']
            var roleUserLogged = this.$store.getters['employee/getEmployee'](user)
            var levelRoleUserLogged = false
            if (roleUserLogged) {
                levelRoleUserLogged = this.$store.getters['employee/getRol'](roleUserLogged.rol)
            }
            return levelRoleUserLogged
        },
        apiURL() {
            return this.$store.getters['login/getAPIurl']
        },
        lateralMenu() {
            return this.$store.state['menu_tools_is_expanded']
        },
        isApp() {
            var bIsApp = process.env.VUE_APP_IS_APP == 'TRUE'
            // if (!bIsApp) {
            //     this.showMoreStates = true
            // }
            return bIsApp
        },
        appURL() {
            return process.env.VUE_APP_APP_URL
        },
        collections() {
            return this.$store.getters['label/getCollections']
        },
        categories() {
            let categories = { ...this.$store.getters['timerproduct/getCategories'] }
            delete categories[false]
            return categories
            // return false;
        },
        sortedCategories() {
            return this.$store.getters['timerproduct/getSortedCategories']
        },
        category() {
            let category = this.$store.getters['timerproduct/getSelectedCategory']
            if (category.id == false) {
                return false
            }
            return category
        },
        products() {
            return this.$store.getters['timerproduct/getProductsOrdered']
            // return undefined;
        },
        activeKpiFilter() {
            return this.$store.getters['timerproduct/getActiveKpiFilter']
        },
        /**
         * Mapa reactivo producto→tiene timer. Se invalida al mutar state.timers.
         */
        productIdsWithTimer() {
            return this.$store.getters['timerproduct/getProductIdsWithTimer']
        },
        productsFiltered() {
            var rows = {}
            var row = 1
            var item = 0

            var max_items = this.num_columns

            var show_cat = !this.category.id
            // Dependencias explícitas: filtro + mapa de timers (se re-evalúa al crear/mover/borrar)
            var kpiFilter = this.activeKpiFilter
            var productIdsWithTimer = this.productIdsWithTimer

            for (var idx in this.products) {
                var label = this.products[idx]
                var show = true

                if (this.category) {
                    show = label.category_id == this.category.id
                }
                if (show && this.stateSelected && this.stateSelected !== PORTRAIT_STATUS_ALL) {
                    show = label.states[this.stateSelected] != undefined && label.states[this.stateSelected].visible
                }

                var labelName = cleanString(label.name.trim().toLowerCase())
                var searchInput = cleanString(this.searchInput.trim().toLowerCase())
                var searchWords = searchInput
                // var searchWords = searchInput.split(' ').join('|')

                if (show && searchInput != '') {
                    const searchRegex = new RegExp(searchWords, 'g')
                    show = labelName.match(searchRegex)
                }

                if (show && kpiFilter) {
                    var hasTimer = !!productIdsWithTimer[String(label.id)]
                    if (kpiFilter === 'with_timer') {
                        show = hasTimer
                    } else if (kpiFilter === 'without_timer') {
                        show = !hasTimer
                    }
                }

                if (show) {
                    // Label to show

                    if (item == 0) {
                        rows[row] = {
                            id: row,
                            columns: max_items,
                            items: [],
                            show_cat: show_cat
                        }
                    }
                    rows[row].items.push(label)
                    if (item < max_items - 1) {
                        item++
                    } else {
                        row++
                        item = 0
                    }
                }
            }

            return Object.values(rows)
        },

        /**
         * Productos visibles en la vista actual (filas del virtual-list aplanadas).
         */
        visibleProducts() {
            return this.productsFiltered.reduce((acc, row) => {
                if (row && Array.isArray(row.items)) {
                    acc.push(...row.items)
                }
                return acc
            }, [])
        },
        multiSelectedCount() {
            return Object.keys(this.multiSelectedProducts).length
        },
        multiSelectedProductsList() {
            return Object.values(this.multiSelectedProducts)
        },
        /**
         * Máximo 3 miniaturas; el resto se resume en el badge +N.
         */
        multiSelectedPreviewProducts() {
            return this.multiSelectedProductsList.slice(0, MULTI_SELECT_PREVIEW_LIMIT)
        },
        multiSelectedOverflowCount() {
            return Math.max(0, this.multiSelectedCount - MULTI_SELECT_PREVIEW_LIMIT)
        },
        showSelectAllProductsButton() {
            return this.isMultiSelectMode && this.multiSelectedCount > 0
        },
        showMultiSelectNextButton() {
            return this.isMultiSelectMode && this.multiSelectedCount > 0
        },
        /**
         * BUILD-7627: en portrait el scroll es del padre (filtros + lista).
         * Hay que pintar todas las filas para que los selectores no queden fijos.
         */
        portraitListKeeps() {
            return Math.max(8, this.productsFiltered.length)
        },
        /**
         * Altura del listado restando barra de filtros, KPI, select-all y footer multi
         * (viven fuera del scroll para no solaparse con las cards).
         */
        labelContainerHeightStyle() {
            const selectAllOffset = this.showSelectAllProductsButton ? 48 : 0
            const nextOffset = this.showMultiSelectNextButton ? 120 : 0
            const baseOffset = this.statesHeight + this.kpiCardsOffset + selectAllOffset + nextOffset

            if (this.printerError) {
                return `height: calc(100% - 95px - (${baseOffset + this.messageHeight}px));`
            }

            return `height: calc(100% - (75px + ${baseOffset}px));`
        },
        areAllVisibleProductsSelected() {
            const visible = this.visibleProducts
            if (visible.length === 0) {
                return false
            }
            return visible.every((product) => !!this.multiSelectedProducts[product.id])
        },
        /**
         * BUILD-7687: fila más baja sin miniaturas para que el virtual-list no deje huecos.
         */
        productListEstimateSize() {
            return this.showImages ? PRODUCT_LIST_ROW_SIZE : PRODUCT_LIST_ROW_SIZE_NO_IMAGES
        },
        labelListExtraProps() {
            // multiSelectedCount fuerza recálculo al mutar el mapa con Vue.set/delete
            const selectedProductIds = { ...this.multiSelectedProducts }
            void this.multiSelectedCount

            return {
                type: 'timerproduct',
                isMultiSelectMode: this.isMultiSelectMode,
                selectedProductIds,
                showImages: this.showImages === true,
                onActivateMultiSelect: this.activateMultiSelect,
                onToggleProductSelect: this.toggleProductSelect
            }
        },

        selecting() {
            return Object.keys(this.selectedLab).length > 0
        },
        selectedLabLength() {
            return Object.keys(this.selectedLab).length
        },

        letters() {
            for (var i in this.products) {
                var lab = this.products[i]
                var first = lab.name.charAt(0).toUpperCase()

                if (Object.keys(this.fbar).indexOf(first) == -1) {
                    this.fbar[first] = []
                }
                this.fbar[first].push(lab.id)
            }
            return this.fbar
        },

        productStates() {
            var products
            if (this.category) {
                products = { ...this.category.references }
            } else {
                products = { ...this.products }
            }

            var states = {}
            for (var i in products) {
                var label = products[i]
                label.image != false ? (this.labelsNoPhoto = false) : true
                for (var j in label.states) {
                    if (!states[j] && label.states[j] && label.states[j].visible) {
                        states[j] = label.states[j]
                    }
                }
            }

            var statesStorted = Object.values(states).sort((a, b) => (parseInt(a.priority) > parseInt(b.priority) ? 1 : -1))

            return statesStorted
        },

        bCategories() {
            return Object.keys(this.categories).length > 0
        },

        home() {
            return this.$store.getters['getHome'] ? this.$store.getters['getHome'] : '/home'
        },

        tpRound() {
            return this.$store.getters.getTimerProductRound
        },

        /**
         * Categorías para el dropdown portrait (sin la entrada "false" del store).
         */
        portraitFilterCategories() {
            const categories = Array.isArray(this.sortedCategories) ? this.sortedCategories : Object.values(this.sortedCategories || {})
            return categories.filter((category) => category && category.id !== false)
        },

        /**
         * Estados de producto para el dropdown (sin caducado/to_delete, id: false).
         */
        portraitFilterStatuses() {
            const states = Array.isArray(this.allProductStates) ? this.allProductStates : Object.values(this.allProductStates || {})
            return states.filter((status) => status && status.id !== false)
        },

        /**
         * Colecciones habilitadas del cliente; si está vacío el dropdown no se renderiza.
         * Las temporales (BUILD-7488) no se listan: se destruyen al terminar el flujo.
         */
        portraitFilterCollections() {
            return Object.values(this.collections || {}).filter((collection) => collection && !collection.temporal)
        },

        currentCollection() {
            if (this.sSelectedCollection === -1 || this.sSelectedCollection === false || this.sSelectedCollection == null) {
                return null
            }
            return this.collections[this.sSelectedCollection] || null
        },

        isCurrentCollectionTemporal() {
            return Boolean(this.temporalCollectionId) || isTemporalCollection(this.currentCollection)
        },

        temporalCollectionPrintId() {
            return this.temporalCollectionId || (this.isCurrentCollectionTemporal ? this.sSelectedCollection : '')
        },

        portraitSelectedCategoryId() {
            return this.category && this.category.id !== false ? this.category.id : false
        },

        /**
         * BUILD-7643: con el filtro de colecciones oculto, portrait muestra
         * el grid de productos (equivalente a sSelectedCollection === -1).
         * Desktop / landscape no usan este computed.
         */
        showPortraitProductGrid() {
            return !SHOW_PORTRAIT_COLLECTION_FILTER || this.sSelectedCollection == -1
        }
    },
    watch: {
        $route: 'load',
        selecting: 'changeSelection',
        selectedLabLength: 'changeSelectionLength',
        allProductStates: 'syncStatusFilterWithCategory',
        recommendedProductIds: {
            immediate: true,
            handler(productIds) {
                if (!productIds || !productIds.length) {
                    return
                }

                this.$nextTick(() => {
                    this.startRecommendedAddProducts(productIds)
                })
            }
        },

        lateralMenu(newCount, oldCount) {
            if (newCount) {
                this.$popup.close()
            } else {
                this.load()
            }
        }
    },
    methods: {
        clickState(state) {
            if (!state) {
                this.allStates = true
            } else {
                this.stateSelected = state
                this.stateSwitch = false
            }
        },
        statesSwitch(val) {
            if (val == 'return') {
                this.allStates = false
            } else if (val) {
                // alert('hi')
                // this.stateSelected = false
            }
            this.stateSwitch = val
        },

        unselectCollection() {
            this.removeTemporalCollections()
            this.temporalCollectionId = null
            if (this.sSelectedCollection != -1) {
                this.$bar.reset()
                this.$bar.hide()
            }
            this.sSelectedCollection = -1
        },
        setTabulation(number) {
            this.iTabulationType = number
        },

        /**
         * Si el estado elegido no está en la categoría actual, el listado
         * quedaría vacío y el dropdown mostraría “todos” (fallback).
         */
        syncStatusFilterWithCategory() {
            if (!this.stateSelected || this.stateSelected === PORTRAIT_STATUS_ALL) {
                return
            }

            const stillValid = this.allProductStates.some((status) => status && status.id == this.stateSelected)
            if (!stillValid) {
                this.stateSelected = false
                this.allStates = true
            }
        },

        /**
         * BUILD-7555: adapta el dropdown de categorías a clickCat existente.
         * No altera productsFiltered; solo dispara la selección ya usada en portrait.
         */
        onPortraitFilterCategory(categoryId) {
            this.setTabulation(1)
            this.unselectCollection()
            this.stateSwitch = false
            this.allStates = false

            if (categoryId === false || categoryId === null) {
                this.clickCat(false)
                this.allCategories = true
                this.catSelPortrait = true
                return
            }

            const category = this.categories[categoryId] || this.portraitFilterCategories.find((item) => item.id === categoryId)
            if (category) {
                this.clickCat(category)
            }
        },

        /**
         * BUILD-7555: adapta el dropdown de estados a stateSelected / clickState.
         */
        onPortraitFilterStatus(statusId) {
            this.setTabulation(1)
            this.unselectCollection()
            this.stateSwitch = false

            if (statusId === PORTRAIT_STATUS_ALL || statusId === false || statusId === null || statusId === '') {
                this.stateSelected = false
                this.allStates = true
                return
            }

            this.clickState(statusId)
        },

        /**
         * BUILD-7555: adapta el dropdown de colecciones a setTabulation + clickCollection.
         */
        onPortraitFilterCollection(collectionId) {
            if (collectionId === -1 || collectionId === false || collectionId === null) {
                this.unselectCollection()
                this.setTabulation(1)
                this.allCategories = true
                this.catSelPortrait = true
                return
            }

            this.setTabulation(2)
            this.clickCollection(collectionId)
        },

        goToConfig() {
            this.$router.push({
                name: 'Config',
                params: {
                    page: 'printer'
                }
            })
        },

        gotoURL(url) {
            if (url) window.open(url, '_blank')
        },

        currentEmployee() {
            var emp_id = this.$store.getters['loginUser/getLocalEmployee']

            if (emp_id) {
                var emp = this.$store.getters['employee/getEmployee'](emp_id)
            } else {
                var emp = this.$store.getters['loginUser/getLocalLocation']
            }

            return emp
        },

        checkAccess() {
            var self = this
        },

        load(refresh = true) {
            var self = this

            this.checkAccess()

            self.$store.dispatch('timerproduct/loadTimerProducts', {})

            self.$bar.reset()
            self.$bar.hide()
        },

        letterScroll(letter) {
            var scroll = {
                container: this.$refs.container,
                target: document.getElementById(this.fbar[letter][0])
            }
            this.$root.scrollTo(scroll)

            this.noscroll = true

            this.current_letter = letter

            this.highlightLetter(letter)

            var self = this
            this.scrolling = setTimeout(function () {
                self.noscroll = false
            }, 500)
        },

        getScroll() {
            if (((this.category == false && Object.keys(this.products).length > 20) || (this.category != false && Object.keys(this.category.references).length > 20)) && !this.noscroll) {
                if (this.scrollWait) {
                    this.scrollWait = true
                }
                window.clearTimeout(this.scrolling)

                var self = this
                this.scrolling = setTimeout(function () {
                    self.handleScroll()
                    self.scrollWait = false
                }, 500)
            }
        },

        handleScroll(event) {
            var scroll_position = this.$refs.container.scrollTop

            var position = 1
            var finded = false
            while (position < Object.keys(this.products).length && !finded) {
                var item_position = this.$refs.container.children[position].offsetTop

                if (scroll_position <= item_position) {
                    finded = true
                } else position++
            }
            var letter = this.products[this.$refs.container.children[position].id].name.charAt(0).toUpperCase()
            this.current_letter = letter
            this.highlightLetter(letter)
        },

        highlightLetter(letter) {
            this.highlight_letter = letter

            window.clearTimeout(this.highlight)

            var self = this
            this.highlight = setTimeout(function () {
                self.highlight_letter = false
            }, 2000)

            var scroll = {
                container: this.$refs.letters,
                offset: -(this.$refs.letters.offsetHeight / 2 - 50),
                target: document.getElementById(letter)
            }
            this.$root.scrollTo(scroll)
        },

        labelClass(label) {
            var result = ''
            if (Object.keys(this.selectedLab).indexOf(label.id) != -1) {
                result = 'selected'
            } else if (label.name.charAt(0).toUpperCase() == this.highlight_letter && this.noscroll == true) {
                result = 'highlight'
            }

            if (this.labelsNoPhoto) {
                result = result + ' no-photo'
            }
            return result
        },
        /**
         * Anula el debounce de searchBar. Si no, un clear/Escape en <500ms
         * deja searchInput con la query anterior y el listado filtrado
         * sin texto visible en el header.
         */
        cancelPendingSearch() {
            if (this.timerSearch) {
                clearTimeout(this.timerSearch)
                this.timerSearch = null
            }
        },
        searchBar(searchInput) {
            this.cancelPendingSearch()
            this.timerSearch = setTimeout(() => {
                this.timerSearch = null
                this.searchInput = searchInput
            }, 500)
        },
        /**
         * Cableado del header (BUILD-7561): actualiza el input y reutiliza searchBar.
         */
        onHeaderSearch(searchInput) {
            this.debounceSearchInput = searchInput
            this.searchBar(searchInput)
        },
        /**
         * Al abrir la búsqueda se muestra el grid de productos.
         * Con colección seleccionada el grid está oculto; hay que salir de esa vista.
         * En el picker de estados hay que ir a "todos los estados"; si no, clickCat
         * deja stateSwitch activo y la lista de estados tapa los productos.
         */
        onHeaderSearchOpen() {
            if (this.sSelectedCollection != -1) {
                this.unselectCollection()
                this.setTabulation(1)
                this.allCategories = true
                this.catSelPortrait = true
            }
            if (this.stateSwitch && !this.category) {
                this.clickState(false)
                return
            }
            if (!this.category && !this.allCategories) {
                this.clickCat('allCategories')
            }
        },

        changeSelection() {
            var self = this
            if (this.selecting) {
                this.$bar.show()
            } else {
                this.$bar.hide()
            }
        },
        changeSelectionLength() {
            var self = this
            self.$bar.addAction('print-info', {
                label: String(self.selectedLabLength) + ' ' + self.$tc('label.selected', self.selectedLabLength),
                opened: true,
                expand: false
            })
            this.changeSelection()
        },

        clickLabel(label) {
            var self = this
            this.currentLabelIndex = label.index
            if (!this.holding && this.selecting) {
                Vue.delete(this.selectedLab, label.id)
            } else if (!this.holding) {
                self.printQueue.push(label)
                self.printPopup()
            } else if (Object.keys(this.selectedLab).length <= 0) {
                this.holding = false
            }
            // hack to avoid prop errors
            this.labelStateSelected = this.stateSelected ? this.stateSelected : ''
        },

        /**
         * Activa el modo selección múltiple (long-press 1s) y marca el producto.
         * Solo UI; las acciones masivas van en BUILD-7488.
         */
        activateMultiSelect(label) {
            if (!label || label.id === undefined || label.id === null) {
                return
            }
            // Absorbe el click sintético posterior al long-press (~200ms + release)
            this.multiSelectIgnoreToggleProductId = label.id
            this.multiSelectIgnoreToggleUntil = Date.now() + 600
            this.isMultiSelectMode = true
            Vue.set(this.multiSelectedProducts, label.id, label)
        },
        /**
         * Toggle de selección en modo multi. Sale del modo si no queda ninguno.
         */
        toggleProductSelect(label) {
            if (!label || label.id === undefined || label.id === null) {
                return
            }
            if (!this.isMultiSelectMode) {
                return
            }
            if (
                label.id === this.multiSelectIgnoreToggleProductId &&
                Date.now() < this.multiSelectIgnoreToggleUntil
            ) {
                return
            }

            if (this.multiSelectedProducts[label.id]) {
                Vue.delete(this.multiSelectedProducts, label.id)
            } else {
                Vue.set(this.multiSelectedProducts, label.id, label)
            }

            if (this.multiSelectedCount === 0) {
                this.isMultiSelectMode = false
            }
        },
        selectAllVisibleProducts() {
            this.visibleProducts.forEach((product) => {
                Vue.set(this.multiSelectedProducts, product.id, product)
            })
            this.isMultiSelectMode = true
        },
        deselectAllVisibleProducts() {
            this.visibleProducts.forEach((product) => {
                if (this.multiSelectedProducts[product.id]) {
                    Vue.delete(this.multiSelectedProducts, product.id)
                }
            })
            if (this.multiSelectedCount === 0) {
                this.isMultiSelectMode = false
            }
        },
        clearMultiSelect() {
            this.isMultiSelectMode = false
            this.multiSelectedProducts = {}
        },

        /**
         * BUILD-7493: entra al wizard existente con los productos sugeridos.
         * Un producto reutiliza `clickLabel`; varios reutilizan la colección temporal.
         *
         * @param {Array<string|number>} productIds
         * @returns {Promise<void>}
         */
        async startRecommendedAddProducts(productIds) {
            if (this.isStartingRecommendedAdd || this.isPreparingTemporalCollection || this.labelSelected) {
                return
            }

            const ids = Array.isArray(productIds) ? productIds : []
            if (ids.length === 0) {
                return
            }

            this.isStartingRecommendedAdd = true
            this.$emit('recommended-consumed')

            try {
                const getProduct = this.$store.getters['timerproduct/getProducts']
                const products = ids
                    .map((id) => resolveProductFromStore(getProduct, { id }))
                    .filter((product) => product && product.id != null && product.states)

                if (products.length === 0) {
                    return
                }

                if (products.length === 1) {
                    this.openedFromRecommendation = true
                    this.clickLabel(products[0])
                    return
                }

                this.clearMultiSelect()
                products.forEach((product) => {
                    Vue.set(this.multiSelectedProducts, product.id, product)
                })
                this.isMultiSelectMode = true
                await this.confirmMultiSelectProducts()
                if (!this.labelSelected && !this.temporalCollectionId) {
                    this.clearMultiSelect()
                    this.openedFromRecommendation = false
                    return
                }
                this.openedFromRecommendation = true
            } finally {
                this.isStartingRecommendedAdd = false
            }
        },

        /**
         * BUILD-7488: Siguiente abre un wizard por producto (como colección).
         * Se configura uno a uno y al final se crean todos los timers.
         */
        async confirmMultiSelectProducts() {
            if (this.isPreparingTemporalCollection || this.multiSelectedCount === 0) {
                return
            }

            const getProduct = this.$store.getters['timerproduct/getProducts']
            const selectedProducts = this.multiSelectedProductsList
                .map((product) => resolveProductFromStore(getProduct, product))
                .filter((product) => product && product.id != null)

            // BUILD-7688: categoría/estado solo filtran el listado. El wizard
            // recibe todos los seleccionados; si un producto tiene el estado
            // activo se reutiliza, si no entra con su primer estado usable.
            const preferredStateId = this.stateSelected && this.stateSelected !== PORTRAIT_STATUS_ALL ? this.stateSelected : null
            let references = buildWizardProductReferences(selectedProducts, { preferredStateId })
            if (references.length === 0) {
                references = buildWizardProductReferences(selectedProducts, {
                    preferredStateId,
                    includeHidden: true
                })
            }

            if (references.length === 0) {
                return
            }

            references = references.map((reference) => ({
                ...reference,
                productSource: 'timerproduct'
            }))

            this.isPreparingTemporalCollection = true
            if (this.$overlay && this.$overlay.loading) {
                this.$overlay.loading()
            }

            try {
                const collection = ensureCollectionStateIds(await this.$store.dispatch('label/prepareTemporalCollection', references))
                if (!collection || !collection.id || !collection.references || Object.keys(collection.references).length === 0) {
                    if (collection && collection.id) {
                        this.$store.dispatch('label/removeTemporalCollection', collection.id)
                    }
                    return
                }

                const statesToPrint = buildPrintStatesFromCollection(collection)
                if (statesToPrint.length === 0) {
                    this.$store.dispatch('label/removeTemporalCollection', collection.id)
                    return
                }

                await this.$store.dispatch('label/loadMultiplePrint', {
                    categories: { [collection.id]: collection }
                })

                this.temporalCollectionId = collection.id
                this.sSelectedCollection = collection.id
                this.labelStateSelected = ''
                this.$bar.reset()
                this.$bar.hide()
                this.labelSelected = true
                this.aStatesToPrint = statesToPrint
                this.clearMultiSelect()
            } catch (error) {
                logError(error)
                this.exitTemporalCollection()
            } finally {
                this.isPreparingTemporalCollection = false
                if (this.$overlay && this.$overlay.hide) {
                    this.$overlay.hide()
                }
            }
        },

        /**
         * Elimina colecciones temporales del store. `keepId` se conserva
         * (la que acabamos de crear o la que el usuario está viendo).
         */
        removeTemporalCollections(keepId) {
            const collections = this.collections || {}
            Object.keys(collections).forEach((id) => {
                if (id === keepId) {
                    return
                }
                if (isTemporalCollection(collections[id])) {
                    this.$store.dispatch('label/removeTemporalCollection', id)
                }
            })
        },

        exitTemporalCollection() {
            const collections = this.collections || {}
            const collectionId = this.temporalCollectionId || this.sSelectedCollection
            const restoreProductList = shouldRestoreProductListAfterWizard({
                temporalCollectionId: this.temporalCollectionId,
                selectedCollectionId: this.sSelectedCollection,
                currentCollection: this.currentCollection,
                collections
            })

            this.temporalCollectionId = null
            if (restoreProductList) {
                this.sSelectedCollection = -1
                this.setTabulation(1)
            }
            this.$bar.reset()
            this.$bar.hide()
            if (collectionId && isTemporalCollection(collections[collectionId])) {
                this.$store.dispatch('label/removeTemporalCollection', collectionId)
            }
        },

        isTemporalCollectionFlow() {
            return Boolean(this.temporalCollectionId) || isTemporalCollection(this.currentCollection)
        },

        /**
         * Un único snackbar: el plugin reutiliza una instancia, así que
         * disparar progress y error seguidos oculta cuántos timers se añadieron.
         *
         * @param {{ created: number, failed: number }} result
         */
        notifyBulkCreateResult(result) {
            if (!result) {
                return
            }
            if (typeof this.onRecommendationAddResult === 'function') {
                this.onRecommendationAddResult(result)
                return
            }
            const created = result.created
            const failed = result.failed
            if (created > 0 && failed > 0) {
                this.$snackbar.warning({
                    closeable: true,
                    message: i18n.t('timersproduct.timers_add_partial', {
                        added: created,
                        failed
                    })
                })
                return
            }
            if (created > 0) {
                this.$snackbar.progress({
                    closeable: true,
                    message: i18n.tc('timersproduct.timers_added', created, { n: created })
                })
                return
            }
            if (failed > 0) {
                this.$snackbar.error({
                    closeable: true,
                    message: i18n.tc('timersproduct.timers_add_failed', failed, { n: failed })
                })
            }
        },

        timerPreparerAll(printer) {
            const items = snapshotPrintAllItems(printer && printer.multipleLabelsToPrint)
            if (items.length === 0) {
                return
            }

            let oParams = {}
            if (printer && printer.oParams && typeof printer.oParams === 'object') {
                try {
                    oParams = JSON.parse(JSON.stringify(printer.oParams))
                } catch (error) {
                    oParams = { ...printer.oParams }
                }
            }
            const printerSnapshot = {
                autoDateCalc: printer && printer.autoDateCalc,
                bUpdatedPrintDate: printer && printer.bUpdatedPrintDate,
                digitalState: printer && printer.digitalState,
                oParams
            }

            this.isConfirmingAdd = true
            this.$emit('createTimerInit')
            const createAll = async () => {
                let created = 0
                let failed = 0
                for (let index = 0; index < items.length; index++) {
                    try {
                        const payload = mapPrintAllItemToTimerPayload(items[index], printerSnapshot)
                        await this.timerPreparer({
                            ...payload,
                            skipCancel: true,
                            skipOverlay: true
                        })
                        created += 1
                    } catch (error) {
                        logError(error)
                        failed += 1
                    }
                }
                return { created, failed }
            }

            return createAll()
                .then((result) => {
                    this.notifyBulkCreateResult(result)
                })
                .catch((error) => {
                    logError(error)
                })
                .finally(() => {
                    this.releaseRecommendationConfirmFlags()
                    this.$emit('timerCreated')
                    this.$emit('createTimerEnd')
                })
        },
        productImageSrc(product) {
            if (!product) {
                return this.appURL + 'img/offline/no_icon_light.svg'
            }
            if (this.isApp && product.local_image) {
                return product.local_image
            }
            if (product.image) {
                return product.image
            }
            return this.appURL + 'img/offline/no_icon_light.svg'
        },
        multiSelectThumbIconStyle(product) {
            const imageSrc = this.productImageSrc(product)
            // JSON.stringify escapa caracteres que romperían url(...)
            return {
                backgroundImage: 'url(' + JSON.stringify(imageSrc) + ')'
            }
        },

        selectLabel(label) {
            if (this.holding) {
                if (Object.keys(this.selectedLab).indexOf(label.id) == -1) {
                    Vue.set(this.selectedLab, label.id, label)
                    this.selectedLab[label.id] = label
                } else {
                    Vue.delete(this.selectedLab, label.id)
                    delete this.selectedLab[label.id]
                }
            }
        },

        selectStateFilter(state) {
            this.$store.commit('timerproduct/selectCategory', false)
            this.clickCat(false)
            this.stateSelected = state
        },

        clickCat(category) {
            var container = document.getElementsByClassName('categories-container')[0]
            if (container) container.scrollTop = 0

            if (category !== 'allCategories') {
                this.allCategories = false
                this.catSelPortrait = false
                if (this.cantSelectCategoryPrinterError) {
                    return false
                } else {
                    if (this.activePredef) {
                        this.activePredef = false
                        this.selectedLab = {}
                    }
                    if (!this.holdingCat) {
                        this.$store.commit('timerproduct/selectCategory', category)
                    } else {
                        this.holdingCat = false
                    }
                    // BUILD-7555: categoría y estado se combinan. Si el estado
                    // deja de existir en la categoría, syncStatusFilterWithCategory lo limpia.
                }
            } else {
                this.allCategories = true
                this.catSelPortrait = true
            }

            this.debounceSearchInput = ''
            this.searchInput = ''
        },

        clickCollection(id) {
            this.removeTemporalCollections(id)
            this.sSelectedCollection = id
            if (!isTemporalCollection((this.collections || {})[id])) {
                this.temporalCollectionId = null
            }
            this.checkShowPrintButton()
            // Solo existe en template portrait; en desktop/landscape no hay nodo.
            var container = document.getElementsByClassName('categories-container')[0]
            if (container) container.scrollTop = 0
        },

        clickPredefined(predef) {
            this.$store.commit('timerproduct/selectCategory', false)
            this.selectedLab = {}
            Object.assign(this.selectedLab, predef.references)
            this.activePredef = predef.id
        },

        selectCat(category) {
            if (this.holdingCat) {
                for (var i in this.products) {
                    var label = this.products[i]
                    if (label.category_id == category.id) {
                        this.holding = true
                        this.holdingCat = false
                        Vue.set(this.selectedLab, label.id, label)
                        this.selectedLab[label.id] = label
                    }
                }
            }
        },
        getSelectedCatLab(cat_id) {
            var result = 0
            for (var i in this.selectedLab) {
                var label = this.selectedLab[i]
                if (label.category_id == cat_id) result++
            }
            return result
        },

        printPopup() {
            this.$bar.reset()
            this.$bar.hide()
            this.labelSelected = this.printQueue[0]
            this.aStatesToPrint = []
            this.aStatesToPrint.push(this.labelSelected)
            this.$snackbar.close()

            // INIT WIZARD PROCESS SAVE TIME SPENT
            this.timetoPrintW.i = moment()
        },

        timerPreparer(timer) {
            // END WIZARD PROCESS SAVE TIME SPENT
            this.timetoPrintW.e = moment()
            const serverTimeDifference = this.$store.getters['getServerTimeDifference']

            var self = this

            var now = moment().add(serverTimeDifference, 'minutes')
            var genericParams = {
                employee: self.currentEmployee().initials,
                employee_name: self.currentEmployee().name + ' ' + self.currentEmployee().surname,
                product: timer.productName,
                note: timer.autoDateCalc ? timer.oParams.note : timer.label.note ? timer.label.note : '',
                note1: timer.autoDateCalc ? timer.oParams.note1 : timer.label.note1 ? timer.label.note1 : '',
                note2: timer.autoDateCalc ? timer.oParams.note2 : timer.label.note2 ? timer.label.note2 : '',
                note3: timer.autoDateCalc ? timer.oParams.note3 : timer.label.note3 ? timer.label.note3 : '',
                print_date: timer.bUpdatedPrintDate ? timer.oParams.print_date + ' *' : now.format('DD/MM/YYYY HH:mm'),
                print_date_day: timer.bUpdatedPrintDate ? timer.oParams.print_date_day : now.format('DD/MM/YYYY'),
                print_date_time: timer.bUpdatedPrintDate ? timer.oParams.print_date_time : now.format('HH:mm'),
                bUpdatedPrintDate: timer.bUpdatedPrintDate,
                digitalState: timer.digitalState,
                autoDateCalc: timer.autoDateCalc
            }

            //iterate timer.oParams and if key not exists in genericParams assign it, only if key doesn't contain 'date'
            Object.keys(timer.oParams).forEach((key) => {
                if (!genericParams[key] && !key.includes('date') && !['state', 'quantity', 'cantidad'].includes(key)) {
                    genericParams[key] = timer.oParams[key]
                }
            })

            if (genericParams.autoDateCalc) {
                Object.keys(timer.oParams).forEach((key) => {
                    if (key.includes('_timestamp')) {
                        var keyAux = key.replace('_timestamp', '')
                        if (timer.oParams[keyAux]) {
                            genericParams[keyAux] = timer.oParams[keyAux]
                        }
                    } else if (key.includes('usodate')) {
                        genericParams[key] = timer.oParams[key]
                    } else if (key.match(/ingr.{1,2}date/gi) || key.match(/ingr.{1,2}rec/gi)) {
                        genericParams[key] = timer.oParams[key]
                    }
                })
            }

            this.printQueue = this.printQueue.slice(1)

            var printDate = timer.bUpdatedPrintDate || timer.autoDateCalc ? timer.oParams.print_date : false

            const timezone = this.$store.getters['getTimeZoneName']
            let date = moment()
            try {
                const options = {
                    timeZone: timezone,
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false
                }
                const copy = moment(date)

                const dateTimeFormat = new Intl.DateTimeFormat([], options).formatToParts()
                const objectDate = {}
                for (const item of dateTimeFormat) {
                    objectDate[item.type] = item.value
                }
                const dateString = `${objectDate['day'].padStart(2, '0')}/${objectDate['month'].padStart(2, '0')}/${objectDate['year']} ${objectDate['hour'].padStart(2, '0')}:${objectDate['minute'].padStart(2, '0')}`
                date = moment(dateString, 'DD/MM/YYYY HH:mm')

                if (date.diff(copy, 'minutes') != serverTimeDifference) {
                    const difference = serverTimeDifference - date.diff(copy, 'minutes')
                    date = date.add(difference, 'minutes')
                }

                printDate = date
            } catch (error) {
                logError(error)
                printDate = moment()
            }

            return self.generateTimer(timer.data, timer.processData, genericParams, printDate, {
                skipCancel: timer.skipCancel === true,
                skipOverlay: timer.skipOverlay === true
            })
        },

        timerCancel() {
            this.printQueue = []
            this.labelSelected = false
            this.aStatesToPrint = []
            const shouldExitTemporal = shouldRestoreProductListAfterWizard({
                temporalCollectionId: this.temporalCollectionId,
                selectedCollectionId: this.sSelectedCollection,
                currentCollection: this.currentCollection,
                collections: this.collections || {}
            })
            if (shouldExitTemporal) {
                this.exitTemporalCollection()
            }
            this.$nextTick(function () {
                this.calcGrid()
                if (this.$refs.labelItems) {
                    this.$refs.labelItems.scrollToIndex(this.currentLabelIndex)
                }
            })
        },
        /**
         * Tras un alta: suelta isConfirmingAdd. Si el wizard de print ya no
         * está abierto, también termina la sesión de sugerencia para que un
         * Cancelar posterior vuelva al listado, no al board.
         */
        releaseRecommendationConfirmFlags() {
            this.isConfirmingAdd = false
            if (!this.labelSelected) {
                this.openedFromRecommendation = false
            }
        },
        /**
         * Cancelar cierra el wizard. Si viene de una sugerencia y no se está
         * confirmando el alta, vuelve al board (no al listado).
         */
        onWizardUnselect() {
            const shouldReturnToBoard = this.openedFromRecommendation && !this.isConfirmingAdd
            this.timerCancel()
            if (shouldReturnToBoard) {
                this.openedFromRecommendation = false
                this.isConfirmingAdd = false
                this.clearMultiSelect()
                this.$emit('closeProducts')
                return
            }

            this.releaseRecommendationConfirmFlags()
        },

        // oProcess -> Pasos del popup
        // oProduct -> Informacion del producto (label, selected state...)
        // defaultParams -> Valores por defecto (Empleado, F. impresion...)
        // printDate -> Fecha de impresion (para que en los next state sea 100pre la misma)
        generateTimer(oProcess, oProduct, defaultParams = {}, printDate = false, options = {}) {
            const skipCancel = options && options.skipCancel === true
            const skipOverlay = options && options.skipOverlay === true

            if (!skipOverlay) {
                this.$emit('createTimerInit', oProduct)
            }
            this.timetoPrintP.i = moment()

            var self = this
            const serverTimeDifference = this.$store.getters['getServerTimeDifference']

            // If we have configured the printer
            var current_config = this.$store.getters['printer/getCurrentPrinter']
            var currentPrinterConfig = this.$store.getters['printer/getPrinterConfig'](current_config)
            if (current_config || true) {
                // --- Definicion de variables

                self.activePrint = true // Inactivate button to print

                self.printData = [] // Data we'll print
                self.num_copies = 1
                self.next_state = false // Next state of selected state
                self.oParams = defaultParams

                var label = oProduct.label
                self.oParams.category = self.categories[label.category_id] ? self.categories[label.category_id].name : ''

                var states = label.states ? label.states : false
                var selState = oProduct.selectedState.id ? oProduct.selectedState.id : false
                var stateMsg = label.states[selState].msg
                var stateInfo = label.states[selState].info != null && label.states[selState].info != 'null' ? label.states[selState].info : {}

                var time = states[selState] && !isNaN(states[selState].time) ? parseInt(states[selState].time) : 0
                var preptime = states[selState] && !isNaN(states[selState].preptime) ? parseInt(states[selState].preptime) : 0

                var format = self.$store.getters['printer/getTemplates'](label.states[selState].format)
                if (format.printer_id != currentPrinterConfig.model) {
                    var templates = this.$store.getters['printer/getTemplatesByPrinterModel'](currentPrinterConfig.model)
                    var bFound = false
                    for (var t in templates) {
                        var tpl = templates[t]
                        const label = this.$store.getters['printer/getLabels'](tpl.label_id)

                        if (tpl.parent_id == format.id && ((typeof currentPrinterConfig.templates[tpl.label_id] !== 'undefined' && currentPrinterConfig.platform == 'android') || (label.tpl_file_app && label.tpl_file_app.includes('.z')) || self.$store.getters['printer/getModels'](currentPrinterConfig.model)['sdk'] != 'brother' || !self.$store.getters.getIsApp)) {
                            format = tpl
                            bFound = true
                        }
                        //TODO bFound == FALSE -> SHOW ERROR
                    }
                }

                var formatValues = format ? JSON.parse(format.data) : []
                var newPreviewStructure = format.realistic_preview ? JSON.parse(format.realistic_preview) : false

                // --- Set de valores que ya conocemos

                self.oParams['state'] = states[selState].name
                self.next_state = states[selState].next
                var oLocation = self.$store.getters['loginUser/getLocalLocation']
                self.oParams['account'] = oLocation.account_name
                self.oParams['location'] = oLocation.name
                self.oParams['quantity'] = self.num_copies
                self.oParams['city'] = oLocation.city
                self.oParams['address'] = oLocation.address
                self.oParams['loc_state'] = oLocation.state
                self.oParams['cp'] = oLocation.cp

                for (var key in stateInfo) {
                    self.oParams[key] = stateInfo[key]
                }

                // --- Format data from process, and create auxiliary info (date_da, date_time...)
                var bCaducidadIsBarcode = false
                for (var i in oProcess) {
                    var step = oProcess[i]

                    if (step.keyname == 'quantity') {
                        self.oParams[step.keyname] = step.result
                        self.num_copies = step.modified ? step.original : parseInt(step.result)
                    } else if (step.type == 'date' || step.type == 'datetime' || step.type == 'datepast' || step.type == 'datetimefree' || step.type == 'datefree' || step.type == 'barcode' || step.type == 'datetimepast') {
                        if (step.type == 'datetime' || step.type == 'datetimefree' || step.type == 'datefree' || step.type == 'datetimepast') {
                            step.result = moment(step.result, 'DD/MM/YYYY HH:mm')
                        } else if (step.type == 'barcode') {
                            if (step.keyname == 'caducidad') bCaducidadIsBarcode = true

                            if (step.result && step.result.trim() !== '') {
                                self.oParams[step.keyname] = step.result
                                if (step.result.toString().length == 10) {
                                    step.result = moment(step.result * 1000)
                                } else {
                                    step.result = moment(false)
                                }
                            }
                        } else if (step.result) {
                            // TODO CHECK IF THIS IS CORRECT
                            step.result = moment(step.result, 'DD/MM/YYYY HH:mm').endOf('day')
                            // step.result = moment(step.result, 'DD/MM/YYYY HH:mm')
                        }

                        // We create different variables (date, date_day...)
                        let aux = step.result
                        if (aux && aux.isValid()) {
                            if (step.type !== 'barcode') self.oParams[step.keyname] = aux.format('DD/MM/YYYY HH:mm')
                            else self.oParams[step.keyname + '_datetime'] = aux.format('DD/MM/YYYY HH:mm')

                            self.oParams[step.keyname + '_day'] = aux.format('DD/MM/YYYY')
                            self.oParams[step.keyname + '_time'] = aux.format('HH:mm')
                            self.oParams[step.keyname + '_timestamp'] = parseInt(aux.valueOf() / 1000).toString()
                            self.oParams[step.keyname + '_d1'] = aux.isoWeekday() == 1 ? 'x' : ' '
                            self.oParams[step.keyname + '_d2'] = aux.isoWeekday() == 2 ? 'x' : ' '
                            self.oParams[step.keyname + '_d3'] = aux.isoWeekday() == 3 ? 'x' : ' '
                            self.oParams[step.keyname + '_d4'] = aux.isoWeekday() == 4 ? 'x' : ' '
                            self.oParams[step.keyname + '_d5'] = aux.isoWeekday() == 5 ? 'x' : ' '
                            self.oParams[step.keyname + '_d6'] = aux.isoWeekday() == 6 ? 'x' : ' '
                            self.oParams[step.keyname + '_d7'] = aux.isoWeekday() == 7 ? 'x' : ' '

                            for (let i = 0; i < 4; i++) {
                                let day = i18n.t('calendar.days')[aux.day()].toUpperCase()
                                if (i > 0) {
                                    self.oParams[step.keyname + '_wd' + i] = day.slice(0, i)
                                } else {
                                    self.oParams[step.keyname + '_wd'] = day
                                }
                            }
                        } else if (!self.oParams.autoDateCalc) {
                            if (step.type !== 'barcode' || step.result == '') self.oParams[step.keyname] = ''
                            else self.oParams[step.keyname + '_datetime'] = ''

                            self.oParams[step.keyname + '_day'] = ''
                            self.oParams[step.keyname + '_time'] = ''
                            self.oParams[step.keyname + '_timestamp'] = ''
                            self.oParams[step.keyname + '_d1'] = ' '
                            self.oParams[step.keyname + '_d2'] = ' '
                            self.oParams[step.keyname + '_d3'] = ' '
                            self.oParams[step.keyname + '_d4'] = ' '
                            self.oParams[step.keyname + '_d5'] = ' '
                            self.oParams[step.keyname + '_d6'] = ' '
                            self.oParams[step.keyname + '_d7'] = ' '

                            for (let i = 0; i < 4; i++) {
                                if (i > 0) {
                                    self.oParams[step.keyname + '_wd' + i] = ' '
                                } else {
                                    self.oParams[step.keyname + '_wd'] = ' '
                                }
                            }
                        }
                    } else {
                        // We set any other value in their position and result
                        self.oParams[step.keyname] = step.result
                    }
                }

                // Fecha de impresion de etiqueta
                printDate = printDate ? moment(printDate, 'DD/MM/YYYY HH:mm') : moment().add(serverTimeDifference, 'minutes')
                // if (self.tpRound) {
                //     printDate = printDate.roundTo('minute', self.tpRound)
                // }

                self.oParams['print_date'] = printDate.format('DD/MM/YYYY HH:mm')
                self.oParams['print_date_day'] = printDate.format('DD/MM/YYYY')
                self.oParams['print_date_time'] = printDate.format('H:mm')
                self.oParams['print_date_d1'] = printDate.isoWeekday() == 1 ? 'x' : ' '
                self.oParams['print_date_d2'] = printDate.isoWeekday() == 2 ? 'x' : ' '
                self.oParams['print_date_d3'] = printDate.isoWeekday() == 3 ? 'x' : ' '
                self.oParams['print_date_d4'] = printDate.isoWeekday() == 4 ? 'x' : ' '
                self.oParams['print_date_d5'] = printDate.isoWeekday() == 5 ? 'x' : ' '
                self.oParams['print_date_d6'] = printDate.isoWeekday() == 6 ? 'x' : ' '
                self.oParams['print_date_d7'] = printDate.isoWeekday() == 7 ? 'x' : ' '
                self.oParams['print_date_timestamp'] = parseInt(printDate.valueOf() / 1000).toString()

                var currentDay = moment(moment().add(serverTimeDifference, 'minutes').format('DD/MM/YYYY 00:00'), 'DD/MM/YYYY HH:mm')
                self.oParams['currentday'] = currentDay.format('DD/MM/YYYY 00:00')
                self.oParams['currentday_day'] = currentDay.format('DD/MM/YYYY')
                self.oParams['currentday_time'] = currentDay.format('00:00')
                self.oParams['currentday_timestamp'] = parseInt(currentDay.valueOf() / 1000).toString()

                var currentTime = moment(moment().add(serverTimeDifference, 'minutes').format('DD/MM/YYYY HH:mm'), 'DD/MM/YYYY HH:mm')
                self.oParams['currenttime'] = currentTime.format('DD/MM/YYYY HH:mm')
                self.oParams['currenttime_day'] = currentTime.format('DD/MM/YYYY')
                self.oParams['currenttime_time'] = currentTime.format('HH:mm')
                self.oParams['currenttime_timestamp'] = parseInt(currentTime.valueOf() / 1000).toString()

                // Fecha de cáculo de caducidades
                var initDate = self.oParams['initdate'] ? moment(self.oParams['initdate'], 'DD/MM/YYYY HH:mm') : !self.oParams.autoDateCalc ? printDate : moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm')

                // Caducidad secundaria
                const cad2a = self.formatTimeToMax(time)
                let aux = !self.oParams.autoDateCalc ? moment(initDate).add(cad2a.result, cad2a.format) : moment(self.oParams['date'], 'DD/MM/YYYY HH:mm') // Print date + time
                // if (self.tpRound) {
                //     aux = aux.roundTo('minute', self.tpRound)
                // }
                self.oParams['date'] = aux.format('DD/MM/YYYY HH:mm')
                self.oParams['date_day'] = aux.format('DD/MM/YYYY')
                self.oParams['date_time'] = aux.format('HH:mm')
                self.oParams['date_timestamp'] = parseInt(aux.valueOf() / 1000).toString()

                self.oParams['date_raw'] = aux.format('DD/MM/YYYY HH:mm')
                self.oParams['date_raw_day'] = aux.format('DD/MM/YYYY')
                self.oParams['date_raw_time'] = aux.format('HH:mm')
                self.oParams['date_raw_timestamp'] = parseInt(aux.valueOf() / 1000).toString()

                // Caducidad de preparacion (?)
                const freezingTime = self.formatTimeToMax(preptime)
                aux = moment(initDate).add(freezingTime.result, freezingTime.format) // Print date + preptime
                // if (self.tpRound) {
                //     aux = aux.roundTo('minute', self.tpRound)
                // }
                self.oParams['prep_date'] = aux.format('DD/MM/YYYY HH:mm')
                self.oParams['prep_date_day'] = aux.format('DD/MM/YYYY')
                self.oParams['prep_date_time'] = aux.format('HH:mm')
                self.oParams['prep_date_timestamp'] = parseInt(aux.valueOf() / 1000).toString()
                self.oParams['quantity_avaibles'] = self.oParams['quantity']

                // --- Set de valores que han de ser calculados pero que no los introduce
                //     el usuario, por tanto no estan en el process de por si
                for (var field in formatValues) {
                    var value = formatValues[field]

                    // si son formatValues y estan entre [ ]
                    const regex = /[\{|\[][a-zA-Z0-9_\}|\]]*/gi
                    let m

                    while ((m = regex.exec(value)) !== null) {
                        // This is necessary to avoid infinite loops with zero-width matches
                        if (m.index === regex.lastIndex) {
                            regex.lastIndex++
                        }

                        // The result can be accessed through the `m`-variable.
                        m.forEach((match, groupIndex) => {
                            match = match.replace(/[\{\}\[\]]/gi, '')
                            if (match != '' && !Object.keys(self.oParams).includes(match)) {
                                self.oParams[match] = ''
                            }
                        })
                    }
                }

                // --- Se calculan las caducidades y se hacen las debidas validaciones

                // Auxiliary vars
                let cadPrimaria = false
                if (bCaducidadIsBarcode) {
                    cadPrimaria = self.oParams['caducidad'] && self.oParams['caducidad_datetime'] ? moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm') : false
                } else {
                    cadPrimaria = self.oParams['caducidad'] && moment(self.oParams['caducidad'], 'DD/MM/YYYY HH:mm').isValid() ? moment(self.oParams['caducidad'], 'DD/MM/YYYY HH:mm') : false
                }

                if (cadPrimaria) {
                    self.oParams['caducidad_day'] = cadPrimaria.format('DD/MM/YYYY')
                    self.oParams['caducidad_time'] = cadPrimaria.format('HH:mm')
                    self.oParams['caducidad_timestamp'] = parseInt(cadPrimaria.valueOf() / 1000).toString()
                } else if (!bCaducidadIsBarcode) {
                    self.oParams['caducidad'] = ''
                    self.oParams['caducidad_day'] = ''
                    self.oParams['caducidad_time'] = ''
                    self.oParams['caducidad_timestamp'] = ''
                }
                // let cadSecundaria = moment(self.oParams['date']);
                // let cadPreparacion = moment(self.oParams['prep_date']);
                let cadSecundaria = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm')
                let cadPreparacion = moment(self.oParams['prep_date'], 'DD/MM/YYYY HH:mm')

                // Si el producto caduca antes que la cad. secundaria, ponemos la cad primaria como caducidad
                if (cadPrimaria && cadPrimaria.isBefore(cadSecundaria)) {
                    self.oParams['date'] = cadPrimaria.format('DD/MM/YYYY HH:mm')
                    self.oParams['date_day'] = cadPrimaria.format('DD/MM/YYYY')
                    self.oParams['date_time'] = cadPrimaria.format('HH:mm')
                    self.oParams['date_timestamp'] = parseInt(cadPrimaria.valueOf() / 1000).toString()
                }
                // Lo mismo con el preptime
                if (cadPrimaria && cadPrimaria.isBefore(cadPreparacion)) {
                    self.oParams['date'] = cadPrimaria.format('DD/MM/YYYY HH:mm')
                    self.oParams['date_day'] = cadPrimaria.format('DD/MM/YYYY')
                    self.oParams['date_time'] = cadPrimaria.format('HH:mm')
                    self.oParams['date_timestamp'] = parseInt(cadPrimaria.valueOf() / 1000).toString()

                    // No tiene sentido un t. de preparacion si ya ha caducado, asi que lo quitamos
                    self.oParams['prep_date'] = '-'
                    self.oParams['prep_date_day'] = '-'
                    self.oParams['prep_date_time'] = '-'
                    self.oParams['prep_date_timestamp'] = ''
                }

                // It only applies at next state
                // Si la cad. secundaria calculada es anterior a la fecha de impresion, es que ha caducado
                if (cadSecundaria.isSameOrBefore(initDate)) {
                    self.oParams['date'] = self.$t('label.expired')
                    self.oParams['date_day'] = self.$t('label.expired')
                    self.oParams['date_time'] = '--'
                    self.oParams['date_timestamp'] = ''
                }

                // Si el estado tiene un mensaje definido, lo mostramos
                if (stateMsg) {
                    self.oParams['date'] = stateMsg
                    self.oParams['date_day'] = stateMsg
                    self.oParams['date_time'] = '--'
                    self.oParams['date_timestamp'] = ''
                }

                if (moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isValid()) {
                    self.oParams['date_d1'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 1 ? 'x' : ' '
                    self.oParams['date_d2'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 2 ? 'x' : ' '
                    self.oParams['date_d3'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 3 ? 'x' : ' '
                    self.oParams['date_d4'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 4 ? 'x' : ' '
                    self.oParams['date_d5'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 5 ? 'x' : ' '
                    self.oParams['date_d6'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 6 ? 'x' : ' '
                    self.oParams['date_d7'] = moment(self.oParams['date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 7 ? 'x' : ' '
                } else {
                    self.oParams['date_d1'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 1 ? 'x' : ' '
                    self.oParams['date_d2'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 2 ? 'x' : ' '
                    self.oParams['date_d3'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 3 ? 'x' : ' '
                    self.oParams['date_d4'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 4 ? 'x' : ' '
                    self.oParams['date_d5'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 5 ? 'x' : ' '
                    self.oParams['date_d6'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 6 ? 'x' : ' '
                    self.oParams['date_d7'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 7 ? 'x' : ' '

                    self.oParams['print_date_d1'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 1 ? 'x' : ' '
                    self.oParams['print_date_d2'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 2 ? 'x' : ' '
                    self.oParams['print_date_d3'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 3 ? 'x' : ' '
                    self.oParams['print_date_d4'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 4 ? 'x' : ' '
                    self.oParams['print_date_d5'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 5 ? 'x' : ' '
                    self.oParams['print_date_d6'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 6 ? 'x' : ' '
                    self.oParams['print_date_d7'] = moment(self.oParams['print_date'], 'DD/MM/YYYY HH:mm').isoWeekday() == 7 ? 'x' : ' '
                }

                let dateParams = ['print_date', 'currentday', 'currenttime', 'date', 'prep_date']
                dateParams.forEach((date) => {
                    for (let i = 0; i < 4; i++) {
                        aux = moment(self.oParams[date], 'DD/MM/YYYY HH:mm')
                        if (aux.isValid()) {
                            let day = i18n.t('calendar.days')[aux.day()].toUpperCase()
                            if (i > 0) {
                                self.oParams[date + '_wd' + i] = day.slice(0, i)
                            } else {
                                self.oParams[date + '_wd'] = day
                            }
                        } else {
                            // IN CASE INVALID DATE, GENERATE DAY INFO WITH CURRENT DAY
                            let day = i18n.t('calendar.days')[moment().day()].toUpperCase()
                            if (i > 0) {
                                self.oParams[date + '_wd' + i] = day.slice(0, i)
                            } else {
                                self.oParams[date + '_wd'] = day
                            }
                        }
                    }
                })

                // self.oParams['letter_relation'] = String.fromCharCode(65 + Math.floor(Math.random() * 26))
                self.oParams['color_relation'] = this.getRandomColorNumber(oProduct.label.id)
                // self.oParams['color_relation'] = String.fromCharCode(65 + Math.floor(Math.random() * 26))

                // --- Calc de valores custom de cada restaurante
                let bBucleAmasada = false
                let bBlucleAmasadaFinish = false
                for (var key in self.oParams) {
                    var val = self.oParams[key]
                    let keyUsoDate = false
                    if (key.match(/usodate/)) {
                        keyUsoDate = key
                        key = 'usodate'
                    }

                    switch (key) {
                        // PIZZA HUT HACK -----------------------
                        case 'cad_sec_mesa':
                        case 'cad_sec_mesa1':
                            if (initDate.isSame(cadPrimaria, 'day')) {
                                self.oParams[key] = cadPrimaria.format('DD/MM/YYYY HH:mm')
                            } else {
                                self.oParams[key] = initDate.format('DD/MM/YYYY') + i18n.t('label.print.config.close_day')
                            }
                            break

                        case 'cad_sec_mesa2':
                            if (cadSecundaria.isSame(cadPrimaria, 'day') || cadSecundaria.isAfter(cadPrimaria, 'day')) {
                                self.oParams[key] = cadPrimaria.format('DD/MM/YYYY HH:mm')
                            } else {
                                self.oParams[key] = cadSecundaria.format('DD/MM/YYYY') + i18n.t('label.print.config.close_day')
                            }
                            break
                        case 'caducidad2': //SALSA TOMATE - CADUCIDAD 1ª ESPECIAS
                            var stepCad2 = oProcess.filter((step) => {
                                return step.keyname == 'caducidad2'
                            })
                            stepCad2 = stepCad2[0] ? stepCad2[0] : false
                            let caducidad2 = stepCad2 && stepCad2.type == 'barcode' ? moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm') : moment(self.oParams['caducidad2'], 'DD/MM/YYYY HH:mm')
                            if (cadPrimaria.valueOf() > caducidad2.valueOf() && (caducidad2.valueOf() < cadSecundaria.valueOf() || (stateMsg && caducidad2.valueOf() < moment(cadSecundaria.format('DD/MM/YYYY 23:59'), 'DD/MM/YYYY HH:mm').valueOf()))) {
                                cadSecundaria = caducidad2
                                self.oParams['date'] = caducidad2.format('DD/MM/YYYY HH:mm')
                                self.oParams['date_day'] = caducidad2.format('DD/MM/YYYY')
                                self.oParams['date_time'] = caducidad2.format('HH:mm')
                                self.oParams['date_timestamp'] = parseInt(caducidad2.valueOf() / 1000).toString()
                            }
                            if (cadPrimaria && cadPrimaria.isValid() && caducidad2.isValid() && stepCad2.rules && stepCad2.rules.o && stepCad2.rules.pa && stepCad2.rules.v && self.oParams[stepCad2.rules.pa]) {
                                let date_compare = moment()
                                switch (stepCad2.rules.v) {
                                    case 'end_day':
                                        date_compare = moment(printDate.format('DD/MM/YYYY 23:59'), 'DD/MM/YYYY HH:mm')
                                        break

                                    default:
                                        break
                                }
                                switch (stepCad2.rules.o) {
                                    case '<':
                                        self.oParams['caducidad2'] = caducidad2.isBefore(date_compare) ? self.oParams[stepCad2.rules.pa] : caducidad2.format(stepCad2.rules.vformat)
                                        break
                                    case '<=':
                                        self.oParams['caducidad2'] = caducidad2.isSameOrBefore(date_compare) ? self.oParams[stepCad2.rules.pa] : caducidad2.format(stepCad2.rules.vformat)
                                        break
                                    case '>':
                                        self.oParams['caducidad2'] = caducidad2.isAfter(date_compare) ? self.oParams[stepCad2.rules.pa] : caducidad2.format(stepCad2.rules.vformat)
                                        break
                                    case '>=':
                                        self.oParams['caducidad2'] = caducidad2.isSameOrAfter(date_compare) ? self.oParams[stepCad2.rules.pa] : caducidad2.format(stepCad2.rules.vformat)
                                        break
                                }
                            }
                            break
                        // PIZZA HUT HACK -----------------------
                        case 'df_lev_massa_day':
                            if (moment(self.oParams['dflevedacao'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dflevedacao'], 'DD/MM/YYYY')
                                let dfMassa = [false, false, 3, 2, 1, false, false]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_lev_massa = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_lev_massa_day'] = df_lev_massa.format('DD/MM/YYYY')
                                self.oParams['df_lev_massa_time'] = df_lev_massa.format('HH:mm')
                                self.oParams['df_lev_massa_timestamp'] = parseInt(df_lev_massa.valueOf() / 1000).toString()
                            } else {
                                self.oParams['df_lev_massa_day'] = ''
                                self.oParams['df_lev_massa_time'] = ''
                                self.oParams['df_lev_massa_timestamp'] = ''
                            }
                            break
                        case 'df_lev_sides_day':
                            if (moment(self.oParams['dflevedacao'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dflevedacao'], 'DD/MM/YYYY')
                                let dfMassa = [false, false, 3, 2, 1, false, false]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_lev_sides = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_lev_sides_day'] = df_lev_sides.format('DD/MM/YYYY')
                                self.oParams['df_lev_sides_time'] = df_lev_sides.format('HH:mm')
                                self.oParams['df_lev_sides_time'] = df_lev_sides.valueOf()
                            } else {
                                self.oParams['df_lev_sides_day'] = ''
                                self.oParams['df_lev_sides_time'] = ''
                                self.oParams['df_lev_sides_timestamp'] = ''
                            }
                            break
                        case 'df_ta_massa_day':
                            if (moment(self.oParams['dftpambiente'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dftpambiente'], 'DD/MM/YYYY')
                                let dfMassa = [false, false, 4, 3, 2, 1, 0.5]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_ta_massa = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_ta_massa_day'] = df_ta_massa.format('DD/MM/YYYY')
                                self.oParams['df_ta_massa_time'] = df_ta_massa.format('HH:mm')
                                self.oParams['df_ta_massa_timestamp'] = parseInt(df_ta_massa.valueOf() / 1000).toString()
                            } else {
                                self.oParams['df_ta_massa_day'] = ''
                                self.oParams['df_ta_massa_time'] = ''
                                self.oParams['df_ta_massa_timestamp'] = ''
                            }
                            break
                        case 'df_ta_sides_day':
                            if (moment(self.oParams['dftpambiente'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dftpambiente'], 'DD/MM/YYYY')
                                let dfMassa = [false, false, 2, 1.5, 1, 0.5, 0.25]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_ta_sides = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_ta_sides_day'] = df_ta_sides.format('DD/MM/YYYY')
                                self.oParams['df_ta_sides_time'] = df_ta_sides.format('HH:mm')
                                self.oParams['df_ta_sides_timestamp'] = parseInt(df_ta_sides.valueOf() / 1000).toString()
                            } else {
                                self.oParams['df_ta_sides_day'] = ''
                                self.oParams['df_ta_sides_time'] = ''
                                self.oParams['df_ta_sides_timestamp'] = ''
                            }
                            break
                        case 'df_lev_pan_day':
                            if (moment(self.oParams['dflevedacao0'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dflevedacao0'], 'DD/MM/YYYY')
                                let dfMassa = [2.5, 1.5, 1, false, false, false, false]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_lev_pan = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_lev_pan_day'] = df_lev_pan.format('DD/MM/YYYY')
                                self.oParams['df_lev_pan_time'] = df_lev_pan.format('HH:mm')
                                self.oParams['df_lev_pan_timestamp'] = parseInt(df_lev_pan.valueOf() / 1000).toString()
                            } else {
                                self.oParams['df_lev_pan_day'] = ''
                                self.oParams['df_lev_pan_time'] = ''
                                self.oParams['df_lev_pan_timestamp'] = ''
                            }
                            break
                        case 'df_ta_pan_day':
                            if (moment(self.oParams['dftpambiente0'], 'DD/MM/YYYY').isValid()) {
                                let df = moment(self.oParams['dftpambiente0'], 'DD/MM/YYYY')
                                let dfMassa = [4, 4, 3, 3, 2, 2, false]
                                let diff = moment(initDate).diff(df, 'days')
                                let df_ta_pan = moment(initDate).add(dfMassa[diff], 'h')

                                self.oParams['df_ta_pan_day'] = df_ta_pan.format('DD/MM/YYYY')
                                self.oParams['df_ta_pan_time'] = df_ta_pan.format('HH:mm')
                                self.oParams['df_ta_pan_timestamp'] = parseInt(df_ta_pan.valueOf() / 1000).toString()
                            } else {
                                self.oParams['df_ta_pan_day'] = ''
                                self.oParams['df_ta_pan_time'] = ''
                                self.oParams['df_ta_pan_timestamp'] = ''
                            }
                            break
                        case 'usodate':
                            if ((self.oParams['caducidad_datetime'] && self.oParams['caducidad_datetime'] != '' && self.oParams['caducidad2_datetime'] && self.oParams['caducidad2_datetime'] != '') || (!cadPrimaria && self.oParams['caducidad2_datetime'] && self.oParams['caducidad2_datetime'] != '')) {
                                self.oParams[keyUsoDate] = moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm').add(preptime, 'minutes').format('DD/MM/YYYY HH:mm')
                            } else if (self.oParams['caducidad_datetime'] && typeof self.oParams['caducidad2'] == 'undefined' && self.oParams[keyUsoDate] == '') {
                                self.oParams[keyUsoDate] = moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm').add(preptime, 'minutes').format('DD/MM/YYYY HH:mm')
                            } else if (self.oParams[keyUsoDate] != '' && (self.oParams['caducidad_datetime'] == '' || self.oParams['caducidad2_datetime'] == '')) {
                                self.oParams[keyUsoDate] = ''
                            }

                            if (self.oParams[keyUsoDate] != '') {
                                var alseaCad2Calc = moment(printDate.valueOf())
                                var bDateModify = false
                                if (time == 0 && stateMsg != '' && stateMsg != null) {
                                    alseaCad2Calc = moment(alseaCad2Calc.format('DD/MM/YYYY') + ' 23:59:59', 'DD/MM/YYYY HH:mm:ss')
                                    if (alseaCad2Calc.isAfter(moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm'))) {
                                        alseaCad2Calc = moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm')
                                        bDateModify = true
                                    }
                                    if (alseaCad2Calc.isAfter(moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm'))) {
                                        alseaCad2Calc = moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm')
                                        bDateModify = true
                                    }

                                    self.oParams['a_date_calc'] = !bDateModify ? stateMsg : alseaCad2Calc.format('DD/MM/YYYY HH:mm')
                                    self.oParams['a_date_calc_day'] = alseaCad2Calc.format('DD/MM/YYYY')
                                    self.oParams['a_date_calc_time'] = alseaCad2Calc.format('HH:mm')
                                    self.oParams['a_date_calc_timestamp'] = parseInt(alseaCad2Calc.valueOf() / 1000).toString()
                                    self.oParams['a_date_calc_d1'] = alseaCad2Calc.isoWeekday() == 1 ? 'x' : ' '
                                    self.oParams['a_date_calc_d2'] = alseaCad2Calc.isoWeekday() == 2 ? 'x' : ' '
                                    self.oParams['a_date_calc_d3'] = alseaCad2Calc.isoWeekday() == 3 ? 'x' : ' '
                                    self.oParams['a_date_calc_d4'] = alseaCad2Calc.isoWeekday() == 4 ? 'x' : ' '
                                    self.oParams['a_date_calc_d5'] = alseaCad2Calc.isoWeekday() == 5 ? 'x' : ' '
                                    self.oParams['a_date_calc_d6'] = alseaCad2Calc.isoWeekday() == 6 ? 'x' : ' '
                                    self.oParams['a_date_calc_d7'] = alseaCad2Calc.isoWeekday() == 7 ? 'x' : ' '
                                } else {
                                    const alcad2a = self.formatTimeToMax(time)
                                    alseaCad2Calc = alseaCad2Calc.add(alcad2a.result, alcad2a.format)

                                    if (alseaCad2Calc.isAfter(moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm'))) {
                                        alseaCad2Calc = moment(self.oParams['caducidad2_datetime'], 'DD/MM/YYYY HH:mm')
                                    }
                                    if (alseaCad2Calc.isAfter(moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm'))) {
                                        alseaCad2Calc = moment(self.oParams['caducidad_datetime'], 'DD/MM/YYYY HH:mm')
                                    }
                                    self.oParams['a_date_calc'] = alseaCad2Calc.format('DD/MM/YYYY HH:mm')
                                    self.oParams['a_date_calc_day'] = alseaCad2Calc.format('DD/MM/YYYY')
                                    self.oParams['a_date_calc_time'] = alseaCad2Calc.format('HH:mm')
                                    self.oParams['a_date_calc_timestamp'] = parseInt(alseaCad2Calc.valueOf() / 1000).toString()
                                    self.oParams['a_date_calc_d1'] = alseaCad2Calc.isoWeekday() == 1 ? 'x' : ' '
                                    self.oParams['a_date_calc_d2'] = alseaCad2Calc.isoWeekday() == 2 ? 'x' : ' '
                                    self.oParams['a_date_calc_d3'] = alseaCad2Calc.isoWeekday() == 3 ? 'x' : ' '
                                    self.oParams['a_date_calc_d4'] = alseaCad2Calc.isoWeekday() == 4 ? 'x' : ' '
                                    self.oParams['a_date_calc_d5'] = alseaCad2Calc.isoWeekday() == 5 ? 'x' : ' '
                                    self.oParams['a_date_calc_d6'] = alseaCad2Calc.isoWeekday() == 6 ? 'x' : ' '
                                    self.oParams['a_date_calc_d7'] = alseaCad2Calc.isoWeekday() == 7 ? 'x' : ' '
                                }

                                let iMarginTime = keyUsoDate.replace('usodate', '')
                                let bInMarginTime = true
                                if (iMarginTime != '') {
                                    bInMarginTime = printDate.isBefore(moment(self.oParams[keyUsoDate], 'DD/MM/YYYY HH:mm').add(iMarginTime, 'minutes'))
                                }

                                if (printDate.isBefore(moment(self.oParams[keyUsoDate], 'DD/MM/YYYY HH:mm')) || !bInMarginTime || (bDateModify && printDate.isAfter(alseaCad2Calc)) || alseaCad2Calc.isBefore(printDate)) {
                                    self.$snackbar.error({
                                        message: i18n.t('tooltip.print_date_out_range'),
                                        closeable: true
                                    })
                                    self.current_error = true
                                }
                            } else {
                                self.oParams['a_date_calc'] = ''
                                self.oParams['a_date_calc_day'] = ''
                                self.oParams['a_date_calc_time'] = ''
                                self.oParams['a_date_calc_timestamp'] = ''
                                self.oParams['a_date_calc_d1'] = ' '
                                self.oParams['a_date_calc_d2'] = ' '
                                self.oParams['a_date_calc_d3'] = ' '
                                self.oParams['a_date_calc_d4'] = ' '
                                self.oParams['a_date_calc_d5'] = ' '
                                self.oParams['a_date_calc_d6'] = ' '
                                self.oParams['a_date_calc_d7'] = ' '
                            }
                            break
                        case 'amasada': //DP Obrador logic
                            if (self.oParams['amasada'] && self.oParams['quantity'] > 0) {
                                bBucleAmasada = true
                                self.oParams['amasada'] = parseInt(self.oParams['amasada']) + (self.num_copies - parseInt(self.oParams['quantity']))

                                self.oParams['quantity'] = parseInt(self.oParams['quantity']) - 1
                                self.num_copies = 1

                                oProcess.forEach((step) => {
                                    if (step.keyname == 'quantity') {
                                        if (!step.modified) {
                                            step.modified = true
                                            step.original = step.result
                                        }
                                        step.result = self.oParams['quantity']
                                    }
                                })
                            } else {
                                bBlucleAmasadaFinish = true
                            }
                            break
                        default:
                            if (key.match(/ingr.{1,2}date$/gi) && self.oParams[key] != '') {
                                let caducidadIngr = moment(self.oParams[key], 'DD/MM/YYYY HH:mm')
                                if ((cadPrimaria.valueOf() > caducidadIngr.valueOf() || !cadPrimaria) && caducidadIngr.valueOf() < cadSecundaria.valueOf()) {
                                    cadSecundaria = caducidadIngr
                                    self.oParams['date'] = caducidadIngr.format('DD/MM/YYYY HH:mm')
                                    self.oParams['date_day'] = caducidadIngr.format('DD/MM/YYYY')
                                    self.oParams['date_time'] = caducidadIngr.format('HH:mm')
                                    self.oParams['date_timestamp'] = parseInt(caducidadIngr.valueOf() / 1000).toString()

                                    let day = i18n.t('calendar.days')[caducidadIngr.day()].toUpperCase()
                                    for (let i = 0; i < 4; i++) {
                                        if (i > 0) {
                                            self.oParams['date_wd' + i] = day.slice(0, i)
                                        } else {
                                            self.oParams['date_wd'] = day
                                        }
                                    }
                                }
                            }
                            break
                    }
                }

                // --- Seteamos los datos que iran al template de la etiqueta
                for (var field in formatValues) {
                    var value = formatValues[field]

                    if (value) {
                        let aux = value

                        let start = aux.indexOf('[') > -1 ? aux.indexOf('[') : aux.indexOf('{')
                        start++
                        let end = aux.indexOf(']') > -1 ? aux.indexOf(']') : aux.indexOf('}')
                        let keyValue = aux.substring(start, end)
                        let oldKeyValue = aux.substring(start, end)

                        while (keyValue) {
                            keyValue = keyValue.replace('[', '').replace(']', '').replace('{', '').replace('}', '')
                            aux = aux.replace('[', '').replace(']', '').replace('{', '').replace('}', '')

                            // if (!self.oParams[keyValue]) {
                            //     self.oParams[keyValue] = aux
                            // }

                            // let newValue = self.oParams[keyValue] // cleanString(self.oParams[keyValue])
                            // if (newValue.indexOf(oldKeyValue) != -1) {
                            //     newValue = newValue.replace(oldKeyValue, '')
                            //     self.oParams[keyValue] = newValue
                            // }
                            aux = aux.replace(keyValue, self.oParams[keyValue])

                            if (newPreviewStructure) {
                                newPreviewStructure.fields.forEach((el) => {
                                    if (el.f_id && el.f_id == field) {
                                        if (el.format && el.type === 'barcode') {
                                            aux = moment(moment(aux, 'DD/MM/YYYY HH:mm').format(el.format), el.format).isValid() ? moment(aux, 'DD/MM/YYYY HH:mm').format(el.format) : aux
                                        }
                                    } else if (!el.f_id && el.value == value && el.format && el.type === 'barcode') {
                                        aux = moment(moment(aux, 'DD/MM/YYYY HH:mm').format(el.format), el.format).isValid() ? moment(aux, 'DD/MM/YYYY HH:mm').format(el.format) : aux
                                    }
                                })
                            }

                            if (['print_date', 'print_date_time', 'print_date_day'].includes(keyValue) && defaultParams.bUpdatedPrintDate) {
                                aux += ' *'
                            }

                            start = aux.indexOf('[') > -1 ? aux.indexOf('[') : aux.indexOf('{')
                            start++
                            end = aux.indexOf(']') > -1 ? aux.indexOf(']') : aux.indexOf('}')
                            keyValue = aux.substring(start, end)
                            oldKeyValue = aux.substring(start, end)
                        }
                        const previewField = newPreviewStructure && newPreviewStructure.fields.find(el => el.f_id ? el.f_id == field : el.value == value)
                        if (previewField && previewField.format && previewField.type !== 'barcode') {
                            aux = formatPrintedDateFromStore(aux, previewField.format)
                        }
                        var data = aux // cleanString(aux)
                        data = data.trim() == '' ? ' ' : data
                        self.printData.push({ k: field, v: data })
                    } else {
                        self.printData.push({ k: field, v: ' ' })
                    }
                }

                // -- Mostraremos en el preview los mismos valores que se quieren imprimir
                let preview = {}
                for (var field in formatValues) {
                    var value = formatValues[field]

                    if (value.indexOf('[') != -1) {
                        let start = value.indexOf('[')
                        let end = value.indexOf(']')
                        let keyValue = value.substring(start, end).replace('[', '').replace(']', '')

                        let name = self.$t('label.preview.' + keyValue)
                        if (name.indexOf('label.preview.') != -1) {
                            name = keyValue
                        }

                        if (keyValue != '' && !Object.keys(preview).includes(keyValue) && Object.keys(self.oParams).includes(keyValue)) {
                            preview[keyValue] = {
                                label: name,
                                value: self.oParams[keyValue]
                            }
                        }
                    }
                }

                self.activePrint = false

                for (let key in self.oParams) {
                    if (key.indexOf('_timestamp') != -1) {
                        const val = self.oParams[key]
                        self.oParams[key] =
                            moment(val * 1000)
                                .add(parseInt(moment().format('ss')), 'seconds')
                                .valueOf() / 1000
                    }
                }

                let params = {
                    state: selState,
                    product: oProduct.label.id,
                    quantity: self.num_copies,
                    data: JSON.stringify(self.oParams),
                    platform: process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2,
                    printed_date: moment().format('YYYY-MM-DD HH:mm:ss')
                }
                self.timetoPrintP.e = moment()

                params.timeToPrintW = self.timetoPrintW.e.diff(self.timetoPrintW.i)
                if (self.isApp) params.timeToPrintP = self.timetoPrintP.e.diff(self.timetoPrintP.i)

                this.isConfirmingAdd = true
                const addTimerPromise = self.$store.dispatch('timerproduct/addTimer', params).then((response) => {
                    if (skipOverlay) {
                        return
                    }
                    if (typeof self.onRecommendationAddResult === 'function') {
                        self.onRecommendationAddResult({ created: 1, failed: 0 })
                        return
                    }
                    let msg = i18n.tc('config.printer.success_save', parseInt(self.num_copies))
                    if (parseInt(self.num_copies) > 1) {
                        msg = msg.replace('[num_copies]', self.num_copies)
                    }
                    msg = msg.replace('[product]', oProduct.label.name)
                    self.$snackbar.progress({
                        closeable: true,
                        message: msg
                    })
                })

                // El unselect post-print es síncrono al volver de generateTimer:
                // el flag tiene que seguir en true en ese momento. No esperar a
                // addTimer. Si el wizard de colección sigue abierto, solo se
                // suelta isConfirmingAdd; si ya se cerró, también la sesión de sugerencia.
                if (!skipOverlay) {
                    this.$nextTick(() => {
                        this.releaseRecommendationConfirmFlags()
                    })
                }

                self.activePrint = false

                if (!skipCancel) {
                    // Colección temporal: print.vue avanza al siguiente producto
                    // (states.length > 1) y cierra al terminar. No cortar el wizard aquí.
                    if (this.aStatesToPrint.length > 1 && this.isTemporalCollectionFlow()) {
                        if (!skipOverlay) {
                            self.$emit('timerCreated')
                            this.$emit('createTimerEnd')
                        }
                        return addTimerPromise
                    }
                    self.timerCancel()
                }

                if (!skipOverlay) {
                    self.$emit('timerCreated')
                    this.$emit('createTimerEnd')
                }

                return addTimerPromise
            } else {
                self.activePrint = false
                // TODO - Create global function to check logged-in user permissions
                var emp_id = self.$store.getters['loginUser/getLocalEmployee']
                var emp = self.$store.getters['employee/getEmployee'](emp_id)
                if (emp && emp.type >= 1) {
                    self.$popup.confirm({
                        message: self.$t('label.printer.alert_empty'),
                        title: self.$t('popup.confirm.title'),
                        textSave: self.$t('popup.confirm.text_save'),
                        textCancel: self.$t('label.printer.alert_later'),
                        callSave: function () {
                            self.$router.push('/config/printer')
                        }
                    })
                } else {
                    self.$popup.alert({
                        message: self.$t('label.printer.config_user')
                    })
                }
            }
            // REFRESH QUEUE LABEL PRINT
            // this.labelQueue()
        },

        printNextState(oProcess, oProduct, label) {
            var state = false
            var self = this

            for (var i in oProcess) {
                var step = oProcess[i]

                if (step.keyname == 'state') {
                    state = label.states[self.next_state]
                    // next_date = moment().add(state.time, 'minutes').format('YYYY-MM-DD HH:mm');

                    oProduct.selectedState.id = state.id
                    oProduct.selectedState.name = state.name

                    step.selected = state.id
                    step.result = state.name
                    step.tags[step.selected].next = false

                    // } else if (step.type == 'date') {
                    //   var aux_date = moment(
                    //     self.oParams['date'],
                    //     'DD/MM/YYYY HH:mm'
                    //   );
                    //   (self.oParams['print_date'] = aux_date.format(
                    //     'DD/MM/YYYY HH:mm'
                    //   )),
                    //     (self.oParams['print_date_day'] = aux_date.format(
                    //       'DD/MM/YYYY'
                    //     )),
                    //     (self.oParams['print_date_time'] = aux_date.format(
                    //       'HH:mm'
                    //     )),
                    //     (step.result = self.oParams[step.keyname + '_day'])
                }
            }

            self.generateTimer(oProcess, oProduct, self.oParams, self.oParams['date'])
        },
        printNextAmasada(oProcess, oProduct, label) {
            this.generateTimer(oProcess, oProduct, this.oParams, this.oParams['print_date'])
        },
        doPrintQueue() {
            const self = this
            const isPrinting = self.$store.getters['label/getIsPrinting']

            if (!isPrinting) {
                self.$store
                    .dispatch('label/resetQueue')
                    .then(() => {
                        self.$store
                            .dispatch('label/getFilteredLogsAndroid', {
                                status: [0, 2]
                            })
                            .then((queue) => {
                                if (Object.keys(queue).length > 0) {
                                    self.$store
                                        .dispatch('printer/printQueueLabels', queue)
                                        .then(() => {
                                            let numberOfCopies = 0

                                            for (let index in queue) {
                                                numberOfCopies += parseInt(queue[index].quantity)
                                            }

                                            let msg = i18n.tc('config.printer.success_queue_print', numberOfCopies)
                                            msg = msg.replace('[num_copies]', numberOfCopies)

                                            self.$snackbar.progress({
                                                closeable: true,
                                                message: msg
                                            })

                                            self.doPrintQueue()
                                        })
                                        .catch((error) => {
                                            logError('error print queue label', error)
                                            const appVersion = process.env.VUE_APP_VERSION
                                            self.$store.dispatch('label/addPrintError', {
                                                error: self.printerError,
                                                platform: 1,
                                                version: appVersion
                                            })
                                        })
                                } else {
                                    self.labelQueue()
                                }
                            })
                            .catch((error) => {
                                logError('error get filtered logs android', error)
                            })
                    })
                    .catch((error) => {
                        logError('error reset queue', error)
                    })
            }
        },

        getFirstCollection() {
            let collections = Object.keys(this.$store.getters['label/getCollections'])
            if (this.sSelectedCollection == -1 && typeof collections[0] !== 'undefined') {
                this.sSelectedCollection = collections[0]
            }
        },

        checkShowPrintButton() {
            // check if is not print page
            if (this.aStatesToPrint.length == 0) {
                let collections = this.$store.getters['label/getCollections']
                let collection = collections[this.sSelectedCollection]
                if (!collection || !collection.references) {
                    this.$bar.reset()
                    this.$bar.hide()
                    return false
                }

                // loop for each reference
                for (let key in collection.references) {
                    let reference = collection.references[key]

                    // loop for each state
                    for (let key2 in reference.states) {
                        let state = reference.states[key2]

                        // check if state selected is undefined or state is selected, show button
                        // by default, state is selected and doesn't have selected attribute
                        if (typeof state.selected === 'undefined' || state.selected) {
                            const isTemporal = this.isTemporalCollectionFlow()
                            this.$bar.addAction(isTemporal ? 'next' : 'print', {
                                callback: () => {
                                    this.printStates()
                                },
                                opened: true,
                                label: isTemporal ? i18n.t('timersproduct.next') : i18n.t('label.predefined_print')
                            })
                            return true
                        }
                    }
                }
            }

            this.$bar.reset()
            this.$bar.hide()
            return false
        },

        printStates() {
            let states = []
            let collections = this.$store.getters['label/getCollections']
            let collection = collections[this.sSelectedCollection]
            if (!collection || !collection.references) {
                this.$bar.reset()
                this.$bar.hide()
                return
            }

            // loop for each reference
            for (let key in collection.references) {
                let reference = collection.references[key]

                // loop for each state
                for (let key2 in reference.states) {
                    // get state
                    let state = reference.states[key2]

                    // set only this state in label
                    let oState = {}
                    oState[state.id] = reference.states[state.id]

                    // check if state selected is undefined or state is selected, add to array
                    // by default, state is selected and doesn't have selected attribute
                    if (typeof state.selected === 'undefined' || state.selected) {
                        states.push({
                            id: reference.id,
                            stateID: state.id,
                            collectionID: this.sSelectedCollection,
                            referenceID: reference.id,
                            name: reference.name,
                            state: state.name,
                            image: reference.image,
                            quantity: state.quantity,
                            category_id: reference.category_id,
                            states: oState,
                            note: reference.note
                        })
                    }
                }
            }

            this.labelSelected = true

            this.$bar.reset()
            this.$bar.hide()
            this.aStatesToPrint = states
        },

        // labelImage(label) {
        //     return fnCheckConnection() ? label.image : false
        // },

        calcStates() {
            if (['landscape', 'desktop'].includes(this.$mq)) {
                if (document.getElementById('states')) {
                    this.statesHeight = document.getElementById('states').offsetHeight
                } else this.statesHeight = 60

                if (this.$refs.message) {
                    this.messageHeight = this.$refs.message.clientHeight
                }

                this.updateKpiCardsOffset()

                if (!this.numberOfStatesToShow) {
                    if (this.$refs) {
                        var options = this.$refs.state_options ? this.$refs.state_options.clientWidth : 0
                        var all = this.$refs.state_all ? this.$refs.state_all.clientWidth : 0
                        var more = this.$refs.state_more ? this.$refs.state_more.clientWidth : 0

                        var available = options - all - more - 30 // 30px of margin
                        var counter = 0

                        if (this.$refs.states) {
                            while (this.$refs.states.length > counter && this.$refs.states[counter].clientWidth > 0 && available > this.$refs.states[counter].clientWidth) {
                                available = available - this.$refs.states[counter].clientWidth
                                counter++
                            }
                        }

                        this.numberOfStatesToShow = counter
                    }
                }
            }
        },
        /**
         * Mide la altura real de las KPI Cards (contenido + márgenes)
         * para que el listado no se desborde cuando el label ocupa varias líneas.
         * Si aún no hay DOM medible, conserva la reserva actual (nunca baja a 0).
         */
        updateKpiCardsOffset() {
            const kpiCards = this.$refs.kpiCards
            const el = kpiCards && kpiCards.$el

            if (!el || !el.offsetHeight) {
                if (!this.kpiCardsOffset) {
                    this.kpiCardsOffset = 96
                }
                return
            }

            const styles = window.getComputedStyle(el)
            const marginTop = parseFloat(styles.marginTop) || 0
            const marginBottom = parseFloat(styles.marginBottom) || 0
            this.kpiCardsOffset = Math.ceil(el.offsetHeight + marginTop + marginBottom)
        },

        sortByName(oObject) {
            return Object.values(oObject).sort(function (oObjectA, oObjectB) {
                return oObjectA.name > oObjectB.name ? 1 : -1
            })
        },
        sortByPriority(oObject) {
            return Object.values(oObject).sort(function (oObjectA, oObjectB) {
                return parseInt(oObjectA.priority) > parseInt(oObjectB.priority) ? 1 : -1
            })
        },
        cleanSearch() {
            this.cancelPendingSearch()
            this.searchInput = ''
            this.debounceSearchInput = ''
        },
        closePrintErrorQueue() {
            this.$router.push({ name: 'Label' })
            this.queueSelected = false
            this.labelQueue()
        },
        calcGrid() {
            // Update number of columns
            var width = window.innerWidth
            var max_items = 5
            for (var w in this.breakpoints) {
                if (width < w) {
                    max_items = this.breakpoints[w]
                    break
                }
            }
            this.num_columns = max_items
        },
        calcResize() {
            if (!this.queueSelected && !this.labelSelected) {
                // console.warn('calcResize')
                clearTimeout(this.timer)
                var self = this
                this.timer = setTimeout(function () {
                    self.calcGrid()
                    if (['landscape', 'desktop'].includes(self.$mq)) {
                        self.calcStates()
                    }
                }, 300)
            }
        },
        formatTimeToMax(timeToFormat) {
            if (typeof timeToFormat === 'undefined') return { result: null, format: 'minute' }
            var year = 365 * 24 * 60
            var month = 30 * 60 * 24
            var day = 60 * 24
            var hours = 60
            var result = false
            var format = false

            // if (timeToFormat % year == 0) {
            //     result = Math.floor(timeToFormat / year)
            //     format = 'year'
            // } else if (timeToFormat < year && timeToFormat % month == 0) {
            //     result = Math.floor(timeToFormat / month)
            //     format = 'month'
            // } else if (timeToFormat > year) {
            //     // Se calculan los años enteros y se obtienen los meses restantes, si los meses son enteros, los devuelve
            //     var years = Math.floor(timeToFormat / year)
            //     var months = timeToFormat - years * year
            //     if (months % month == 0) {
            //         result = Math.floor(months / month) + years * 12
            //         format = 'month'
            //     }
            // } else if (timeToFormat % day == 0) {
            //     result = Math.floor(timeToFormat / day)
            //     format = 'day'
            // } else if (timeToFormat % hours == 0) {
            //     result = Math.floor(timeToFormat / hours)
            //     format = 'hour'
            // } else {
            //     result = timeToFormat
            //     format = 'minute'
            // }
            result = timeToFormat
            format = 'minute'
            return { result: result, format: format }
        },

        backToTimers() {
            this.openedFromRecommendation = false
            this.isConfirmingAdd = false
            this.clearMultiSelect()
            this.exitTemporalCollection()
            this.removeTemporalCollections()
            this.$emit('closeProducts')
        },
        getRandomColorNumber(referenceId) {
            const timers = this.$store.getters['timerproduct/getTimers']
            // Filtramos los timers que coinciden con el referenceId
            const filteredTimers = Object.values(timers).filter((timer) => timer.reference_id === referenceId)

            // Si ya no hay números disponibles, recargamos del 1 al 10 y reiniciamos lastNumber
            if (this.availableNumbers.length === 0) {
                this.availableNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                this.lastNumber = filteredTimers.length > 0 ? [...new Set(filteredTimers.map((timer) => timer.data.color_relation))] : []
            }

            // Filtramos para no incluir los números ya usados
            let numbersToChoose = this.availableNumbers.filter((num) => !this.lastNumber.includes(num))

            // Si no quedan números para elegir, reiniciamos todo
            if (numbersToChoose.length === 0) {
                this.availableNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                this.lastNumber = []
                numbersToChoose = this.availableNumbers
            }

            // Elegimos uno al azar
            const randomIndex = Math.floor(Math.random() * numbersToChoose.length)
            const selectedNumber = numbersToChoose[randomIndex]

            // Actualizamos
            this.availableNumbers = this.availableNumbers.filter((num) => num !== selectedNumber)
            this.lastNumber.push(selectedNumber)


            return selectedNumber
        },
        generateNumber() {
            this.currentNumber = this.getRandomColorNumber()
        }
    },
    created() {
        // ORIENTATION LOCK IF SCREEN WIDTH IS LESS THAN 550 *viewport
        if ((window.screen.width < 550 || window.screen.height < 550) && this.isApp) {
            screen.orientation.lock('portrait')
        }

        this.load()
        window.addEventListener('resize', this.calcResize)
    },
    destroyed() {
        this.cancelPendingSearch()
        if (document.getElementById('label_container')) document.getElementById('label_container').removeEventListener('scroll', this.getScroll)
        this.$popup.close()
        window.removeEventListener('resize', this.calcResize)
        this.clearMultiSelect()
        this.exitTemporalCollection()
        this.removeTemporalCollections()

        // ORIENTATION UNLOCK
        screen.orientation.unlock()
    },
    mounted() {
        var self = this

        this.checkAccess()
        // Portrait también necesita calcGrid (num_columns) para pintar el virtual-list.
        this.calcResize()
        if (['landscape', 'desktop'].includes(this.$mq)) {
            if (document.getElementById('label_container')) {
                document.getElementById('label_container').addEventListener('scroll', this.getScroll)
            }
            // Medición inmediata (sin esperar el debounce de calcResize)
            // para alinear el calc del listado con la altura real de las KPI.
            this.$nextTick(() => {
                this.updateKpiCardsOffset()
            })
        }

        this.$root.$on('label', (label) => {
            // your code goes here
            this.clickLabel(label)
        })
    },
    updated: function () {
        this.calcResize()
    },
    beforeRouteLeave(to, from, next) {
        // TODO hide tooltips
        this.$popup.close()
        this.$snackbar.close()
        // clearInterval(this.iIntervalStatusPrinter)
        next()
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
#content.label {
    $fast-bar-width: 35px;
    $size-bolita: rem(18px);
    $icon-size: rem(60px);

    .timer-product-kpi-cards {
        width: auto;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        // Mantener margen lateral: un solo margin-top reseteaba left/right a 0
        // y pegaba la KPI derecha al borde del contenedor.
        margin: 8px;
    }

    .products-multi-next {
        display: flex;
        flex-direction: column;
        width: 100%;
        box-sizing: border-box;
        flex-shrink: 0;
        gap: 8px;
        padding: 0 8px 8px;
        background-color: $default-bg-color;
        position: relative;
        z-index: 6;

        &__summary {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            min-height: 48px;
            padding: 8px 12px;
            box-sizing: border-box;
            background-color: $default-bg-color;
            border-radius: 4px;
            min-width: 0;
        }

        &__thumbs {
            display: flex;
            align-items: center;
            flex: 0 0 auto;
            // Padding para que el anillo/sombra del 1.º y último círculo no se recorte
            padding: 4px 4px;
            margin: -4px 0;
            overflow: visible;
            box-sizing: border-box;
        }

        &__thumb {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: 0;
            outline: none;
            background-color: #fff;
            box-sizing: border-box;
            position: relative;
            flex-shrink: 0;
            overflow: hidden;
            // Anillo blanco sin borde: evita la línea oscura por antialiasing
            box-shadow: 0 0 0 2px #fff;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            transform: translateZ(0);

            &:first-child {
                margin-left: 2px;
            }

            &:not(:first-child) {
                margin-left: -12px;
            }

            &:last-child {
                margin-right: 2px;
            }
        }

        // Caja interna más pequeña: el icono se escala con contain y cabe entero
        &__thumb-icon {
            display: block;
            width: 22px;
            height: 22px;
            flex-shrink: 0;
            background-color: transparent;
            background-repeat: no-repeat !important;
            background-position: center center !important;
            background-size: contain !important;
        }

        &__thumb--overflow {
            color: var(--neutro-s-40-app);
            font-size: 14px;
            font-style: normal;
            font-weight: 700;
            line-height: 20px;
            font-family: $text-bold;
            text-align: center;
        }

        &__count {
            color: $neutro-s90;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 20px;
            font-family: $text-bold;
            white-space: nowrap;
            // No crecer: si flex:1 ocupa todo el hueco, la medición de thumbs queda en 1
            flex: 0 1 auto;
            min-width: 0;
            margin-left: 0;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &__action {
            display: flex;
            width: 100%;
            min-height: 48px;
            padding: 12px 16px;
            justify-content: center;
            align-items: center;
            margin: 0;
            border: 0;
            border-radius: 4px;
            background: $main;
            color: $color-white;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 16px;
            font-family: $text-bold;
            cursor: pointer;

            &:disabled {
                opacity: 0.6;
                cursor: default;
                pointer-events: none;
            }
        }
    }

    .products-select-all {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        width: 100%;
        max-width: 100%;
        padding: 8px 8px 12px;
        box-sizing: border-box;
        flex-shrink: 0;
        // Fuera del scroll: no sticky. Así las cards nunca lo tapan.
        position: relative;
        z-index: 6;
        background-color: $default-bg-color;
        text-align: right;
        min-width: 0;

        &__action {
            display: flex;
            min-height: 32px;
            max-width: 100%;
            min-width: 0;
            padding: 6px 8px;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin: 0;
            border: 0;
            border-radius: 4px;
            background: var(--neutro-light-app);
            cursor: pointer;
            color: $neutro-s90;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 16px;
            font-family: $text-bold;
            box-sizing: border-box;
        }

        &__icon {
            flex-shrink: 0;
            width: 16px;
            height: 16px;
            @include background($image: img('select_all_black.svg'), $size: 16px, $position: center center);
        }

        &__label {
            color: inherit;
            font-size: inherit;
            font-style: inherit;
            font-weight: inherit;
            line-height: inherit;
            white-space: nowrap;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .content.right,
    .content.full {
        position: relative;
        padding-bottom: 0 !important;
        overflow-y: hidden !important;

        .empty-page {
            min-height: 70vh;
            display: grid;
            place-items: center;
        }

        .states {
            margin-top: rem(15px);
            padding-left: rem(6px);
            margin-bottom: rem(5px);
            width: 100%;
            position: static;
            top: 0;
            background-color: $default-bg-color;

            &.alert-active {
                top: 70px;
            }

            .states-title {
                @include font-size(sm);
                font-family: $conden;
                width: 100%;
                text-align: left;
                display: inline-block;
                padding-left: 6px;
                color: $neutro-s70;
            }

            .state-options {
                display: inline-table;
                width: calc(100%);
                height: auto;
                overflow: all;

                .state {
                    @include interaction();
                    display: inline-block;
                    background-color: #fff;
                    padding: 7px 15px;
                    border-radius: 3px;
                    width: max-content;
                    margin: 5px;
                    height: fit-content;
                    font-family: $text;
                    text-transform: uppercase !important;
                    white-space: nowrap;

                    @include bgHover(#fff);

                    &.selected {
                        background-color: $main;
                        color: #fff;

                        @include bgHover($main);
                    }

                    &.hide {
                        display: none;
                    }

                    &.display {
                        @include background($image: img('down_arrow_neutro_s70.svg'), $size: 18px, $position: left 5px center);
                        background-color: darken($default-bg-color, 5%);
                        color: $neutro-s70;
                        text-transform: unset !important;
                        padding-left: 24px;

                        @include bgHover(darken($default-bg-color, 5%));

                        &.less {
                            @include background($image: img('up_arrow_neutro_s70.svg'), $size: 18px, $position: left 5px center);
                        }
                    }

                    &.transparent {
                        background-image: none;
                        background-color: transparent;
                        color: transparent;
                        content: '';
                    }

                    &.back_button {
                        @include background($image: img('left_dark.svg'), $size: 24px, $position: center center);
                        display: inline;
                        padding: 9px 15px;
                    }
                }
            }
        }

        .search-container {
            position: relative;
            padding-right: 11px;
            padding-left: 11px;
            margin: 0;

            .search {
                @include display-flex();
                @include align-items(center);
                @include background($image: img('search_neutro.svg'), $size: 18px, $position: left 12px center);
                @include border-radius(4px);
                @include font-size(ml);
                height: 50px;
                width: 100%;
                background-color: #fff;
                padding: 0 70px 0 40px;
                border-bottom: none;
                font-family: $text;
                cursor: text;
            }

            .close {
                @include border-radius(4px);
                @include background($image: img('close_dark.svg'), $size: 12px, $position: center center);
                width: 33px;
                height: 33px;
                position: absolute;
                top: 10px;
                right: 40px;
                background-color: $neutro-t50;
                cursor: pointer;
            }
        }

        .container {
            @include display-flex();
            @include flex-direction(row);
            @include flex-wrap(wrap);
            @include align-content(flex-start);
            @include align-items();
            max-width: unset;
            width: calc(100%);
            padding: 6px;
            margin: 0;
            float: left;
            overflow: auto;
            height: calc(100% - 60px);
            // margin-top: 60px;
            position: static;
            bottom: 0;

            .virtual-list {
                height: 360px;
                overflow-y: auto;
                width: 100%;
            }

            .list-keep {
                width: 100%;
                height: 100%;
                border-radius: 3px;
                overflow-y: auto;
                & > div {
                    display: flex;
                    flex-wrap: wrap;
                }

                .list-item-keep {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 5px;
                    // width: 100%;
                    // display: flex;
                    // flex-wrap: wrap;
                }
            }

            &.alert-active {
                // margin-top: 130px;
            }

            .item {
                @include interaction();
                @include align-items();
                @include border-radius(3px);
                line-height: 1;
                background-color: #fff;
                text-align: center;
                width: 18% !important;
                margin: 6px 1%;
                /*min-width: 160px;*/
                height: rem(150px);
                padding: 12px;
                position: relative;

                @include bgHover(#fff);

                // @media (max-width: 1200px) {
                //     width: 23% !important;
                // }

                @media (max-width: 1100px) {
                    width: 31% !important;
                }

                @media (max-width: 800px) {
                    width: 48% !important;
                }

                .color {
                    @include border-radius($size-bolita);
                    width: $size-bolita;
                    height: $size-bolita;
                    position: absolute;
                    top: 10px;
                    left: 10px;
                }

                .image {
                    display: block;
                    margin: 10px auto;
                    text-align: center;
                    height: $icon-size;
                    // width: $icon-size;

                    img {
                        // height: $icon-size;
                        // width: $icon-size;
                        height: 100%;
                        // margin: 10px auto;
                        object-fit: contain;
                    }
                }

                .imageapp {
                    @include background($image: img('offline/no_icon_light.svg'));
                    display: block;
                    margin: 10px auto;
                    height: $icon-size;
                    width: $icon-size;

                    &.appempty {
                        @include background($image: img('offline/no_icon_light.svg') !important);
                    }
                }

                .text {
                    @include display-flex();
                    @include align-items();
                    @include justify-content();
                    width: 100%;
                    height: 50px;
                    text-align: center;
                    margin-top: 5px;
                    font-family: $text-bold;
                }

                &.selected {
                    background-color: $accent-t50;
                }

                &.highlight {
                    transition: 0.1s;
                    transform: translate(0, -5px);
                    @include box-shadow($h: 0px, $v: 10px, $b: 10px, $s: -3px, $c: rgba(100, 100, 100, 0.5));
                }

                /*&:active {
          animation-name: itemHold;
          animation-duration: 125ms;
          animation-delay: 175ms;
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
        }*/

                &.no-photo {
                    height: rem(80px);

                    .image {
                        display: none;
                    }

                    .color {
                        top: 30px;
                        margin: 0 10px;
                        left: 0;
                    }

                    .text {
                        @include justify-content(left);
                        width: calc(100% - #{$size-bolita});
                        text-align: left;
                        margin-left: $size-bolita + rem(10px);
                        overflow: hidden;
                        margin-top: 0;
                        height: 100%;
                    }
                }
            }
            @include keyframes(itemHold) {
                100% {
                    background-color: $accent-t50;
                }
            }

            .empty {
                @include background($image: img('empty4.svg'), $size: 70px, $position: top center);
                margin-top: calc(50% - #{$menu-top-height} - 70px);
                padding-top: 80px;
                width: 100%;
            }
        }

        .fast-bar {
            @include display-flex();
            @include flex-direction(row);
            @include flex-wrap(wrap);
            @include align-content(flex-start);
            @include align-items();
            width: $fast-bar-width;
            height: calc(100% - 60px);
            float: right;
            transition: 0.1s;
            position: absolute;
            right: -0px;
            z-index: 50;
            //@include background($color: $default-content-color);
            overflow: hidden;
            bottom: 0;

            &.hide {
                width: 0px;
                transition: 0.5s;
            }

            &:hover {
                width: $fast-bar-width !important;
            }

            .btn {
                @include background($color: transparent);
                font-size: rem(16px);
                color: $main-text;
                line-height: 0;
                font-family: $text-bold;
                display: block;
                padding-top: 5px;
                padding-bottom: 5px;
                width: $fast-bar-width;
                height: calc(100% / 25);

                &.empty {
                    color: #bbb;
                    cursor: default;
                }

                &.active {
                    background-color: $main;
                    color: #fff;
                }

                &.highlight {
                }
            }
        }

        .fast-bar + .container {
            /*width: calc(100% - #{$fast-bar-width});*/
            width: 100%;
        }

        &.right {
            width: calc(100% - 200px) !important;

            .container {
                .item {
                    width: 31%;
                    margin: 6px 1%;
                }
            }

            &.full {
                width: 100% !important;
            }
        }
    }

    .tabulation {
        height: 50px;
        width: 200px !important;
        padding-top: 10px;
        display: flex;

        .category,
        .collection {
            @include interaction();
            @include border-radius(6px);
            flex-grow: 1;

            @include bgHover($default-bg-color);

            &.selected {
                flex-grow: 10;
                background-color: #fff;

                .text {
                    height: 40px;
                    width: 40px;
                    float: left;
                    display: initial;
                    @include font-size(m);
                    font-family: $text-bold;
                    color: $main;
                    display: flex;
                    align-items: center;
                }

                @include bgHover(#fff);
            }
            .icon {
                height: 40px;
                width: 40px;
                float: left;
            }
            .text {
                display: none;
            }
        }

        .category {
            .icon {
                @include background($image: img('label_main.svg'), $size: 20px, $position: center center);
            }
        }

        .collection {
            .icon {
                @include background($image: img('collection_main.svg'), $size: 20px, $position: center center);
            }
        }
    }

    .column.categories {
        @include box-shadow($h: 0px, $v: 0px, $b: 6px, $s: 0px, $c: rgba($neutro-s90, 0.25));
        width: 200px !important;
        height: 100% !important;
        border-radius: 5px;

        &.column-withtab {
            height: calc(100% - 50px) !important;
        }

        .tab {
            @include font-size(sm);
            color: #888;
            font-family: $text;
            text-align: center;
            margin: 0 auto 0 auto;
            padding: 12px 6px;
            width: 50%;
            height: 40px;
            display: inline-block;

            &.selected {
                color: $main-text;
                border-bottom: 2px solid $main-light;
            }
        }

        .item {
            @include interaction();
            @include display-flex();
            @include align-items();
            @include justify-content(space-between);
            @include font-size(m);
            line-height: 1;
            color: #555;
            min-height: rem(60px);
            padding: 4px 12px;
            margin: 4px;
            border-radius: 5px;
            // background-color: #fff;

            .color {
                width: $size-bolita;
                height: $size-bolita;
                border-radius: 100%;
            }

            .text,
            .count {
                display: inline-block;
                font: inherit;
                @include font-size(m);
                font-family: $text;
                line-height: inherit;
                white-space: break-spaces;
                word-break: break-word;
            }

            .text {
                width: calc(100% - #{$size-bolita + rem(10px)});
            }
            .count {
                @include background($color: $main-t80);
                @include border-radius(20px);
                @include font-size(s);
                font-family: $text;
                line-height: 20px;
                min-width: 20px;
                width: auto;
                height: 20px;
                text-align: center;
                padding: 0px 4px;
            }

            @include bgHover($inactive-t90);

            &.selected {
                background-color: $main;

                .text {
                    color: white;
                    font-family: $text-bold;
                }

                .color {
                    filter: brightness(75%);
                }

                @include bgHover($main);
            }

            &.all {
            }

            &:active {
                animation-name: itemHoldCat;
                animation-duration: 125ms;
                animation-delay: 175ms;
                animation-iteration-count: 1;
                animation-fill-mode: forwards;
            }
            @include keyframes(itemHoldCat) {
                100% {
                    background-color: #f9f9f9;
                    border-left-color: $accent-dark;
                }
            }
        }

        .collection {
            @include interaction();
            @include display-flex();
            @include align-items();
            @include justify-content(space-between);
            @include font-size(m);
            line-height: 1;
            color: #555;
            min-height: rem(60px);
            padding: 4px 12px;
            margin: 4px;
            border-radius: 5px;

            @include bgHover($inactive-t90);

            &.selected {
                font-family: $text-bold;
                background-color: $main;
                color: #fff;

                @include bgHover($main);
            }
        }
    }

    #collections {
        padding: 10px 20px;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        align-content: start;
        column-gap: 15px;
        row-gap: 15px;
        align-items: start;
        padding-bottom: 70px;
    }

    @media (max-width: 1300px) {
        #collections {
            grid-template-columns: 1fr 1fr 1fr;
        }
    }

    @media (max-width: 1100px) {
        #collections {
            grid-template-columns: 1fr 1fr;
        }
    }
}
</style>

// PORTRAIT STYLES
<style lang="scss">
.portrait #content.label,
.landscape #content.label {
    #message {
        padding: 0 10px 0 15px;
    }
}
.portrait #content.label {
    $size-bolita: rem(18px);

    .portrait-label-container {
        @include display-flex();
        @include flex-direction(column);
        height: 100%;
        padding: 12px;
        overflow: hidden;

        .timer-product-list-header {
            margin: 0 0 8px;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            flex-shrink: 0;
        }

        .timer-product-kpi-cards {
            margin: 0 0 8px;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            flex-shrink: 0;
            // BUILD-7627: único bloque que permanece fijo al hacer scroll en mobile.
            background-color: $default-bg-color;
            z-index: 2;
        }

        .empty-page {
            // min-height: 70vh;
            height: 100%;
            display: grid;
            place-items: center;
        }

        .content {
            padding: 0px !important;
            width: 100% !important;
            overflow-y: unset !important;

            #message {
                padding: 0;
                padding-left: 10px;
            }
        }
        .categories-container {
            flex: 1 1 auto;
            min-height: 0;
            height: auto;
            width: 100%;
            // BUILD-7627: el scroll incluye filtros + lista. Solo KPI Cards quedan fuera.
            overflow-y: auto;

            .portrait-categories {
                width: 100%;
                height: auto;
                min-height: 100%;

                #collections.right {
                    float: none !important;

                    @media screen and (max-width: 500px) {
                        grid-template-columns: 1fr;
                    }
                    padding-bottom: 80px !important;
                }

                .item {
                    @include interaction();
                    @include display-flex();
                    @include align-items();
                    @include justify-content(space-between);
                    @include font-size(m);
                    line-height: 1;
                    color: #555;
                    min-height: rem(60px);
                    padding: 4px 12px;
                    margin: 0px 0px 4px 0px;
                    border-radius: 5px;
                    background-color: #fff;

                    .color {
                        width: $size-bolita;
                        height: $size-bolita;
                        border-radius: 100%;
                    }
                    .text {
                        font-family: $text-bold;
                    }
                }

                .text,
                .count {
                    display: inline-block;
                    font: inherit;
                    @include font-size(m);
                    font-family: $text;
                    line-height: inherit;
                    white-space: break-spaces;
                    word-break: break-word;
                }

                .text {
                    width: calc(100% - #{$size-bolita + rem(10px)});
                }
                .count {
                    @include background($color: $main-t80);
                    @include border-radius(20px);
                    @include font-size(s);
                    font-family: $text;
                    line-height: 20px;
                    min-width: 20px;
                    width: auto;
                    height: 20px;
                    text-align: center;
                    padding: 0px 4px;
                }

                @include bgHover($inactive-t90);

                &.selected {
                    background-color: $main;

                    .text {
                        color: white;
                        font-family: $text-bold;
                    }

                    .color {
                        filter: brightness(75%);
                    }

                    @include bgHover($main);
                }

                &.all {
                }

                &:active {
                    animation-name: itemHoldCat;
                    animation-duration: 125ms;
                    animation-delay: 175ms;
                    animation-iteration-count: 1;
                    animation-fill-mode: forwards;
                }
                @include keyframes(itemHoldCat) {
                    100% {
                        background-color: #f9f9f9;
                        border-left-color: $accent-dark;
                    }
                }
                .label-content {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    height: auto;
                    min-height: 100%;
                    align-content: flex-start;

                    .products-select-all {
                        flex-shrink: 0;
                        // El botón siempre se ajusta al contenido y queda a la derecha.
                        // En móvil estrecho solo bajamos tipografía y permitimos wrap.
                        @media screen and (max-width: 500px) {
                            padding: 6px 4px 10px;

                            &__action {
                                font-size: 13px;
                                line-height: 15px;
                                padding: 6px;
                                gap: 6px;
                                width: auto;
                                max-width: 100%;
                            }

                            &__icon {
                                width: 14px;
                                height: 14px;
                                @include background($image: img('select_all_black.svg'), $size: 14px, $position: center center);
                            }

                            &__label {
                                white-space: normal;
                                text-align: left;
                                overflow: visible;
                                text-overflow: unset;
                            }
                        }
                    }

                    #label_container {
                        flex: 0 0 auto;
                        min-height: 0;
                        height: auto;
                        overflow: visible;
                    }
                }
                &:hover {
                    background-color: #f9f3f2;
                }
            }
        }

        > .products-multi-next {
            flex-shrink: 0;
            width: 100%;
            max-width: 100%;

            @media screen and (max-width: 500px) {
                padding: 0 4px 8px;

                &__summary {
                    padding: 6px 8px;
                    gap: 4px;
                    min-height: 40px;
                }

                &__count {
                    font-size: 13px;
                    line-height: 16px;
                    white-space: normal;
                    overflow: visible;
                    text-overflow: unset;
                }

                &__action {
                    min-height: 44px;
                    font-size: 15px;
                }
            }
        }

        #label_container {
            height: auto;
            padding: 0;
            overflow: visible;

            .virtual-list {
                height: auto;
                overflow: visible;
                width: 100%;
            }

            .list-keep {
                width: 100%;
                height: auto;
                border-radius: 3px;
                overflow: visible;
                & > div {
                    display: flex;
                    flex-wrap: wrap;
                }

                .list-item-keep {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 5px 0px;
                    // width: 100%;
                    // display: flex;
                    // flex-wrap: wrap;
                }
            }
        }
    }
}
</style>
